import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { API, useAuth } from "@/App";
import { useLocale } from "@/context/LocaleContext";
import { AccountMenu } from "@/components/AccountMenu";
import { teamDisplayName } from "@/lib/teamNames";
import { matchRoundDisplay } from "@/lib/matchRoundDisplay";
import { FlagCircle } from "@/components/Flag";
import { GroupStandingsMatchList } from "@/components/GroupStandingsMatchList";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import BracketView from "@/components/BracketView";
import { TAB_MAIN_TITLE_CLASS } from "@/lib/tab-page-title";
import { buildInviteLink } from "@/lib/inviteJoin";
import { bettorDisplayName } from "@/lib/bettorDisplayName";
import { PROJECTS_LIST_PATH } from "@/lib/routes";
import { buildTopscorerTeamOptions } from "@/lib/topscorerTeamOptions";
import { pickHarryBoyRow, parseScoreLabelForBet, CS_DISPLAY_ROW_COUNT } from "@/lib/harryBoy";
import { formatApiErrorMessage } from "@/lib/apiErrorMessage";
import { kickoffToDate, parseKickoffUtcMs } from "@/lib/kickoffTime";
import { formatMatchTeamLabel } from "@/lib/knockoutPlaceholderDisplay";
import { isGroupPhaseLocked, isMatchTipInputLocked } from "@/lib/matchTipsLocked";
import { buildMockLeaderboard, buildMockStandings, isMockResultsEnabled } from "@/lib/mockResults";
import { calcGroupStandingsFromFixtures, calcBestThirdFromGroups, parseStandingsResponse } from "@/lib/groupStandings";
import { StandingsStatBodyCells, StandingsStatHeaderCells, bestThirdRowClassName } from "@/components/StandingsStatsColumns";
import { effectiveMatchScores, formatMatchScore } from "@/lib/matchScores";
import { useMatchScoreDraft, useScoreInputScrollFlush } from "@/lib/useMatchScoreDraft";
import { MatchPenWinnerMark } from "@/components/MatchPenWinnerMark";
import { fixtureMatchesOverviewQuery, normalizeOverviewSearchQuery } from "@/lib/overviewMatchSearch";
import {
  ChevronLeft,
  Copy,
  Link2,
  Mail,
  RefreshCw,
  Settings as SettingsIcon,
  MoreVertical,
  Target,
  Flag,
  Crosshair,
  Award,
  Trophy,
  Sparkles,
  Trash2,
  UserMinus,
  Loader2,
  LayoutGrid,
  BarChart2,
  Plus,
} from "lucide-react";
import LeaderboardPicksMatrix from "@/components/LeaderboardPicksMatrix";
import LeaderboardChartBlock from "@/components/LeaderboardChartBlock";
import { wc2026BroadcasterSweden } from "@/data/wc2026BroadcastSe";
import {
  CURRENCY_CODES_ORDERED,
  currencySelectLabel,
  formatCurrencyAmount,
} from "@/lib/currencies";
import { computePrizeBreakdown, normalizeSlotForApi } from "@/lib/prizeEconomy";
import { QRCodeSVG } from "qrcode.react";
import {
  buildSwishMessage,
  buildSwishPaymentUrl,
  buildSwishQrContent,
  swishPhoneToInputValue,
  validateSwishPhone,
} from "@/lib/swishPayment";

/** Glass panel: flat full-bleed below sm; card chrome from sm and up (see .glass-mobile-flat). */
const panelCard = "glass glass-mobile-flat rounded-none sm:rounded-2xl";
const panelCardOverflow = `${panelCard} overflow-hidden`;
const SETTINGS_SECTION_DIVIDER = "pt-6 border-t border-[var(--line-2)]";
const SETTINGS_SECTION_TITLE = "text-sm font-semibold text-[var(--accent)] uppercase tracking-wider mb-2";
const SETTINGS_HINT = "text-sm leading-relaxed text-[var(--text-dim)]";
const SETTINGS_META = "text-xs leading-relaxed text-[var(--text-dim)]";
const SETTINGS_LABEL = "text-sm font-medium";
const MEMBER_ROLE_BADGE =
  "text-xs uppercase tracking-wide px-2 py-0.5 rounded-md border border-[var(--accent)]/25 bg-[var(--accent)]/10 text-[var(--accent)] shrink-0 font-semibold";

const EMPTY_ARR = [];

/** Chronological list order (API may order by group / id). */
function byKickoffAsc(a, b) {
  const ta = parseKickoffUtcMs(a.kickoff) ?? Infinity;
  const tb = parseKickoffUtcMs(b.kickoff) ?? Infinity;
  if (ta !== tb) return ta - tb;
  return String(a.id ?? "").localeCompare(String(b.id ?? ""));
}

/** WC2026: en svensk huvudkanal per gruppspelsmatch; slutspel markerat SVT/TV4. */
function WcBroadcastCell({ channel, compact }) {
  const box = compact ? "h-6 w-auto max-h-6 max-w-[5.5rem]" : "h-6 w-full min-w-0 max-w-full";
  const linkCls = compact
    ? "block w-max max-w-full opacity-90 hover:opacity-100 transition-opacity"
    : "block w-full min-w-0 opacity-90 hover:opacity-100 transition-opacity";
  const spanJustify = compact ? "justify-start" : "justify-center";
  const imgBase = compact
    ? "max-h-full w-auto max-w-[5.5rem] object-contain object-left"
    : "max-h-6 w-auto max-w-full object-contain object-center";
  const svtImg = compact ? `${imgBase} origin-top-left scale-105 -translate-x-2` : imgBase;
  const tv4Img = compact ? `${imgBase} origin-top-left scale-95 -translate-x-0.5` : `${imgBase} scale-[0.82] origin-center`;
  if (channel === "svt") {
    return (
      <a href="https://www.svtplay.se" target="_blank" rel="noopener noreferrer" className={linkCls} title="SVT Play">
        <span className={`inline-flex w-full min-w-0 items-center ${spanJustify} ${box}`}>
          <img src="/svt-play.png" alt="SVT Play" className={svtImg} loading="lazy" decoding="async" />
        </span>
      </a>
    );
  }
  if (channel === "tv4") {
    return (
      <a href="https://www.tv4play.se" target="_blank" rel="noopener noreferrer" className={linkCls} title="TV4 Play">
        <span className={`inline-flex w-full min-w-0 items-center ${spanJustify} ${box}`}>
          <img src="/tv4-play.webp" alt="TV4 Play" className={tv4Img} loading="lazy" decoding="async" />
        </span>
      </a>
    );
  }
  if (channel === "both") {
    return <span className="text-[10px] font-mono text-[var(--text-dim)] whitespace-nowrap" title="SVT / TV4">SVT/TV4</span>;
  }
  return <span className="text-[var(--text-dim)] font-mono text-xs">–</span>;
}

const PROJECT_TAB_KEYS = new Set([
  "table",
  "group",
  "standings",
  "bracket",
  "extras",
  "topplistor",
  "settings",
]);

function normalizeProjectTab(raw) {
  if (raw && PROJECT_TAB_KEYS.has(raw)) return raw;
  return "table";
}

export default function ProjectPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const { lang, t } = useLocale();
  const [project, setProject] = useState(null);
  const [bets, setBets] = useState({});
  const [groupBets, setGroupBets] = useState({});
  const [finalsBet, setFinalsBet] = useState({});
  const [standings, setStandings] = useState({});
  const [bestThirdStandings, setBestThirdStandings] = useState([]);
  const [leaderboard, setLeaderboard] = useState({ rows: [], scorers: [], picks_by_player: {} });
  const [picksMatrix, setPicksMatrix] = useState(null);
  const urlTab = normalizeProjectTab(searchParams.get("tab"));
  const [tabUi, setTabUi] = useState(urlTab);
  const [tab, setTab] = useState(urlTab);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [liveStatus, setLiveStatus] = useState("connecting"); // connecting|live|offline
  const [, startTabTransition] = React.useTransition();
  const mockResults = isMockResultsEnabled();
  const [leaderboardHubStatus, setLeaderboardHubStatus] = useState("idle"); // idle | loading | ready
  const tabRef = useRef(tab);
  const leaderboardHubFetchingRef = useRef(false);
  const leaderboardHubStatusRef = useRef("idle");

  useScoreInputScrollFlush();

  useEffect(() => {
    tabRef.current = tab;
  }, [tab]);

  useEffect(() => {
    leaderboardHubStatusRef.current = leaderboardHubStatus;
  }, [leaderboardHubStatus]);

  useEffect(() => {
    setTabUi(urlTab);
    startTabTransition(() => setTab(urlTab));
  }, [id, urlTab, startTabTransition]);

  const handleTabChange = useCallback(
    (next) => {
      const el = document.activeElement;
      if (el && el.tagName === "INPUT" && el.classList?.contains("score-input")) {
        queueMicrotask(() => el.blur());
      }
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next === "table") {
            p.delete("tab");
          } else {
            p.set("tab", next);
          }
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const teamsByCode = useMemo(() => {
    if (!project?.teams) return {};
    return Object.fromEntries(
      project.teams.map((tm) => [tm.code, teamDisplayName(tm.code, tm.name, lang)]),
    );
  }, [project, lang]);

  const logoByCode = useMemo(() => {
    if (!project?.teams) return {};
    return Object.fromEntries(
      project.teams.filter((tm) => tm.logoUrl).map((tm) => [String(tm.code), tm.logoUrl]),
    );
  }, [project]);

  const loadCore = useCallback(async ({ background = false } = {}) => {
    if (!background) {
      leaderboardHubFetchingRef.current = false;
      if (!mockResults) {
        setLeaderboard({
          rows: [],
          scorers: [],
          picks_by_player: {},
          topscorer_picks_by_user: {},
          topscorer_goals_by_user: {},
        });
        setPicksMatrix(null);
        setLeaderboardHubStatus("idle");
      }
    }
    const [p, mb, gb, fb, st] = await Promise.all([
      axios.get(`${API}/projects/${id}`),
      axios.get(`${API}/projects/${id}/bets/match`),
      axios.get(`${API}/projects/${id}/bets/groups`),
      axios.get(`${API}/projects/${id}/bets/finals`),
      mockResults ? Promise.resolve({ data: null }) : axios.get(`${API}/projects/${id}/standings`),
    ]);
    setProject(p.data);
    setBets(Object.fromEntries(mb.data.map((b) => [b.match_id, b])));
    setGroupBets(Object.fromEntries(gb.data.map((g) => [g.group, g])));
    setFinalsBet(fb.data || {});
    if (mockResults) {
      const mockGroups = buildMockStandings(p.data);
      setStandings(mockGroups);
      setBestThirdStandings(calcBestThirdFromGroups(mockGroups));
      setLeaderboard(buildMockLeaderboard(p.data));
      setPicksMatrix({
        members: [],
        match_bets_by_user: {},
        group_bets_by_user: {},
        finals_bets_by_user: {},
        league_bets_by_user: {},
      });
      setLeaderboardHubStatus("ready");
    } else {
      const parsed = parseStandingsResponse(st.data);
      setStandings(parsed.groups);
      setBestThirdStandings(parsed.bestThird);
    }
  }, [id, mockResults]);

  const loadLeaderboardHub = useCallback(async ({ background = false } = {}) => {
    if (mockResults) return;
    if (leaderboardHubFetchingRef.current) return;
    leaderboardHubFetchingRef.current = true;
    if (!background) setLeaderboardHubStatus("loading");
    try {
      const [lb, pm] = await Promise.all([
        axios.get(`${API}/projects/${id}/leaderboard`),
        axios.get(`${API}/projects/${id}/picks-matrix`)
          .then((r) => r.data)
          .catch(() => null),
      ]);
      const lbRaw = lb.data;
      if (Array.isArray(lbRaw)) {
        setLeaderboard({
          rows: lbRaw,
          scorers: [],
          picks_by_player: {},
          topscorer_picks_by_user: {},
          topscorer_goals_by_user: {},
        });
      } else {
        setLeaderboard({
          rows: lbRaw.rows || [],
          scorers: lbRaw.scorers || [],
          picks_by_player: lbRaw.picks_by_player || {},
          topscorer_picks_by_user: lbRaw.topscorer_picks_by_user || {},
          topscorer_goals_by_user: lbRaw.topscorer_goals_by_user || {},
        });
      }
      setPicksMatrix(
        pm ?? {
          members: [],
          match_bets_by_user: {},
          group_bets_by_user: {},
          finals_bets_by_user: {},
          league_bets_by_user: {},
        },
      );
      setLeaderboardHubStatus("ready");
    } catch (e) {
      if (!background) {
        setLeaderboardHubStatus("idle");
        toast.error(formatApiErrorMessage(e, t("auth.errGeneric")) || t("auth.errGeneric"));
      }
    } finally {
      leaderboardHubFetchingRef.current = false;
    }
  }, [id, mockResults, t]);

  /** Live sync / WebSocket: uppdatera i bakgrunden utan att tömma UI eller visa Laddar. */
  const refreshLiveUpdate = useCallback(
    async (msgType) => {
      try {
        if (msgType === "odds_updated") {
          const { data } = await axios.get(`${API}/projects/${id}`);
          setProject(data);
          return;
        }
        const reqs = [axios.get(`${API}/projects/${id}`)];
        if (!mockResults) reqs.push(axios.get(`${API}/projects/${id}/standings`));
        const [p, st] = await Promise.all(reqs);
        setProject(p.data);
        if (!mockResults && st) {
          const parsed = parseStandingsResponse(st.data);
          setStandings(parsed.groups);
          setBestThirdStandings(parsed.bestThird);
        }
        if (
          msgType === "scorers_updated" ||
          msgType === "fixtures_synced" ||
          msgType === "fixture_updated"
        ) {
          if (leaderboardHubStatusRef.current === "ready") {
            await loadLeaderboardHub({ background: true });
          }
        }
      } catch (e) {
        console.warn("[live refresh]", e?.message);
      }
    },
    [id, mockResults, loadLeaderboardHub],
  );

  const load = useCallback(async () => {
    await loadCore();
    if (!mockResults && tabRef.current === "topplistor") {
      await loadLeaderboardHub();
    }
  }, [loadCore, loadLeaderboardHub, mockResults]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (mockResults || tab !== "topplistor") return;
    if (leaderboardHubStatus !== "idle") return;
    loadLeaderboardHub();
  }, [tab, mockResults, leaderboardHubStatus, loadLeaderboardHub]);

  // WebSocket: instant updates on fixture changes (manual or live sync)
  useEffect(() => {
    const wsUrl = API.replace(/^http/, "ws") + `/ws/projects/${id}`;
    let ws, retry, alive = true;
    const connect = () => {
      ws = new WebSocket(wsUrl);
      ws.onopen = () => setLiveStatus("live");
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (
            msg.type === "fixture_updated" ||
            msg.type === "fixtures_synced" ||
            msg.type === "scorers_updated" ||
            msg.type === "odds_updated"
          ) {
            refreshLiveUpdate(msg.type);
          }
        } catch {}
      };
      ws.onclose = () => {
        if (!alive) return;
        setLiveStatus("offline");
        retry = setTimeout(connect, 4000);
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };
    connect();
    const onLogout = () => { try { ws && ws.close(); } catch {} };
    window.addEventListener("auth:logout", onLogout);
    return () => {
      window.removeEventListener("auth:logout", onLogout);
      alive = false;
      clearTimeout(retry);
      try { ws && ws.close(); } catch {}
    };
  }, [id, refreshLiveUpdate]);

  const saveMatchBet = async (match_id, home_score, away_score, options = {}) => {
    const { keepalive = false, silent = false } = options || {};
    const key = String(match_id ?? "");
    const hStr = home_score === null || home_score === undefined ? "" : String(home_score).trim();
    const aStr = away_score === null || away_score === undefined ? "" : String(away_score).trim();
    const hEmpty = hStr === "";
    const aEmpty = aStr === "";
    const postBet = async (payload) => {
      if (keepalive && typeof window !== "undefined" && typeof fetch === "function") {
        const csrf = (() => {
          const raw = document.cookie || "";
          const parts = raw.split(";").map((p) => p.trim());
          for (const p of parts) {
            if (!p) continue;
            const eq = p.indexOf("=");
            const name = eq >= 0 ? p.slice(0, eq) : p;
            const value = eq >= 0 ? p.slice(eq + 1) : "";
            if (name === "csrf_token") return decodeURIComponent(value);
          }
          return "";
        })();
        return fetch(`${API}/projects/${id}/bets/match`, {
          method: "POST",
          credentials: "include",
          keepalive: true,
          headers: {
            "Content-Type": "application/json",
            ...(csrf ? { "X-CSRF-Token": csrf } : {}),
          },
          body: JSON.stringify(payload),
        });
      }
      return axios.post(`${API}/projects/${id}/bets/match`, payload);
    };
    try {
      if (hEmpty && aEmpty) {
        await postBet({
          match_id: key,
          home_score: null,
          away_score: null,
        });
        if (keepalive) return;
        setBets((b) => {
          const next = { ...b };
          delete next[key];
          delete next[match_id];
          return next;
        });
        return;
      }
      const homeVal = hEmpty ? null : Number(hStr);
      const awayVal = aEmpty ? null : Number(aStr);
      if (!hEmpty && !Number.isFinite(homeVal)) {
        toast.error(t("project.saveFailed"));
        return;
      }
      if (!aEmpty && !Number.isFinite(awayVal)) {
        toast.error(t("project.saveFailed"));
        return;
      }
      await postBet({
        match_id: key,
        home_score: homeVal,
        away_score: awayVal,
      });
      if (keepalive) return;
      setBets((b) => ({
        ...b,
        [key]: { match_id: key, home_score: homeVal, away_score: awayVal },
      }));
    } catch (e) {
      if (!silent) {
        toast.error(formatApiErrorMessage(e, t("project.saveFailed")) || t("project.saveFailed"));
      }
    }
  };

  const harryBoyBusyRef = useRef(false);
  const [harryBoyBusy, setHarryBoyBusy] = useState(false);
  const [harryBoyProgress, setHarryBoyProgress] = useState(null);

  const clearAllMatchTips = useCallback(async () => {
    if (!project) return;
    const ok = window.confirm(t("project.clearAllConfirm"));
    if (!ok) return;
    if (harryBoyBusyRef.current) return;
    const fx = project.fixtures || [];
    const keysRaw = Object.keys(bets || {});
    const keysToClear = keysRaw.filter((key) => {
      const m = fx.find((f) => String(f.id) === String(key));
      return m && !isMatchTipInputLocked(m, fx, project?.knockout_tips_unlock_until || null);
    });
    if (keysToClear.length === 0) {
      if (keysRaw.length > 0) toast.error(t("project.clearAllLocked"));
      return;
    }
    harryBoyBusyRef.current = true;
    setHarryBoyBusy(true);
    setHarryBoyProgress(null);
    try {
      const { data } = await axios.delete(`${API}/projects/${id}/bets/match`);
      const deleted = data?.deleted ?? 0;
      if (deleted === 0) {
        if (keysRaw.length > 0) toast.error(t("project.clearAllLocked"));
        return;
      }
      setBets((b) => {
        const next = { ...b };
        for (const k of keysToClear) {
          delete next[k];
        }
        return next;
      });
      toast.success(t("project.clearedAllToast"));
    } finally {
      harryBoyBusyRef.current = false;
      setHarryBoyBusy(false);
      setHarryBoyProgress(null);
    }
  }, [project, bets, id, t]);

  const fillHarryBoy = useCallback(
    async (mode) => {
      if (!project) return;
      const oddsCs = project.odds_cs_top3_by_match_id;
      if (!oddsCs || typeof oddsCs !== "object" || Object.keys(oddsCs).length === 0) {
        toast.error(t("project.harryBoyNoOdds"));
        return;
      }
      if (isGroupPhaseLocked(project.fixtures || [])) {
        toast.error(t("project.harryBoyGroupLocked"));
        return;
      }
      if (harryBoyBusyRef.current) return;
      harryBoyBusyRef.current = true;
      setHarryBoyBusy(true);
      setHarryBoyProgress(null);
      try {
        const gMatches = [
          ...project.fixtures.filter((m) => m.stage === "GROUP" || m.stage === "LP"),
        ].sort(byKickoffAsc);
        const pending = [];
        for (const m of gMatches) {
          const key = String(m.id ?? "");
          const existing = bets?.[key];
          const hHas = existing?.home_score != null && String(existing.home_score) !== "";
          const aHas = existing?.away_score != null && String(existing.away_score) !== "";
          if (hHas && aHas) continue;
          const top = oddsCs[key] ?? oddsCs[m.id];
          const row = pickHarryBoyRow(top, mode);
          if (!row) continue;
          const parsed = parseScoreLabelForBet(row.label);
          if (!parsed) continue;
          pending.push({
            key,
            home: hHas ? Number(existing.home_score) : parsed.h,
            away: aHas ? Number(existing.away_score) : parsed.a,
          });
        }
        if (!pending.length) {
          toast.error(t("project.harryBoyNoCandidates"));
          return;
        }
        setHarryBoyProgress({ done: 0, total: pending.length });
        try {
          const { data } = await axios.post(`${API}/projects/${id}/bets/match/bulk`, {
            bets: pending.map(({ key, home, away }) => ({
              match_id: key,
              home_score: home,
              away_score: away,
            })),
          });
          const n = data?.saved ?? 0;
          if (n === 0) {
            toast.error(t("project.harryBoyNoCandidates"));
            return;
          }
          const updates = Object.fromEntries(
            pending.map(({ key, home, away }) => [key, { match_id: key, home_score: home, away_score: away }]),
          );
          setBets((prev) => ({ ...prev, ...updates }));
          setHarryBoyProgress({ done: n, total: pending.length });
          toast.success(t("project.harryBoyToast", { n }));
        } catch (e) {
          toast.error(formatApiErrorMessage(e, t("project.saveFailed")) || t("project.saveFailed"));
        }
      } finally {
        harryBoyBusyRef.current = false;
        setHarryBoyBusy(false);
        setHarryBoyProgress(null);
      }
    },
    [project, bets, id, t],
  );

  if (!project) return <div className="min-h-screen flex items-center justify-center text-[var(--accent)]">{t("common.loading")}</div>;

  const organizerIds = Array.isArray(project.owner_ids) && project.owner_ids.length
    ? project.owner_ids
    : (project.owner_id ? [project.owner_id] : []);
  const isOwner = !!(user?.id && organizerIds.includes(user.id));
  const adminIds = project.admin_ids || [];
  const isStaff = isOwner || !!(user?.id && adminIds.includes(user.id));
  const isSuperuser = !!user?.is_superuser;
  const groupMatches = [...project.fixtures.filter((m) => m.stage === "GROUP")].sort(byKickoffAsc);
  const koMatches = [...project.fixtures.filter((m) => m.stage !== "GROUP")].sort(byKickoffAsc);

  return (
    <div className="min-h-screen grain pb-20 overflow-x-hidden">
      <header className="border-b border-[var(--line)] px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between sticky top-0 bg-[var(--bg)]/85 backdrop-blur z-30 gap-2 min-w-0">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <Link to={PROJECTS_LIST_PATH} className="btn-ghost flex items-center gap-1 shrink-0"><ChevronLeft size={16}/><span className="hidden sm:inline">{t("project.back")}</span></Link>
          <h1 className="font-display text-xl sm:text-3xl ml-1 sm:ml-2 truncate" data-testid="project-name">{project.name}</h1>
          <span data-testid="ws-status" className={`text-xs flex items-center gap-1.5 ml-1 sm:ml-2 shrink-0 ${liveStatus==="live"?"text-[var(--accent-2)]":liveStatus==="offline"?"text-[var(--text-dim)]":"text-[var(--accent)]"}`}>
            <span className={`w-2 h-2 rounded-full ${liveStatus==="live"?"bg-[var(--accent-2)] animate-pulse":liveStatus==="offline"?"bg-[var(--text-dim)]":"bg-[var(--accent)]"}`}/>
            <span className="hidden sm:inline">{liveStatus==="live"?t("project.live"):liveStatus==="offline"?t("project.offline"):"…"}</span>
          </span>
          {mockResults && (
            <span className="ml-1 sm:ml-2 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border border-[var(--accent)]/50 text-[var(--accent)] bg-[var(--panel)]/40">
              MOCK DATA
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {/* Desktop actions before account menu; mobile sheet after account so ⋮ stays rightmost on small screens */}
          <button data-testid="btn-copy-invite" onClick={() => { navigator.clipboard.writeText(project.invite_code); toast.success(t("project.toastInviteCopied")); }}
                  className="hidden md:flex btn-ghost header-toolbar-btn items-center gap-2"><Copy size={14}/>{t("project.copyCode")} <span className="font-mono text-[var(--accent)]">{project.invite_code}</span></button>
          <button data-testid="btn-copy-invite-link" type="button" onClick={() => { const url = buildInviteLink(project.invite_code); if (url) { navigator.clipboard.writeText(url); toast.success(t("project.toastInviteLinkCopied")); } }}
                  className="hidden md:flex btn-ghost header-toolbar-btn header-toolbar-invite items-center gap-2" title={buildInviteLink(project.invite_code)}><Copy size={14}/>{t("project.copyInviteLink")}</button>
          {isSuperuser && (
            <button data-testid="btn-send-summary" onClick={async()=>{await axios.post(`${API}/projects/${id}/send-summary`); toast.success(t("project.toastMailSent"));}} className="hidden md:flex btn-ghost header-toolbar-btn header-toolbar-mail items-center gap-2"><Mail size={14}/>{t("project.mailToday")}</button>
          )}
          <button onClick={load} className="hidden md:flex btn-ghost shrink-0"><RefreshCw size={14}/></button>
          {/* Mobile menu sheet */}
          <button
            type="button"
            data-testid="header-topplistor"
            onClick={() => handleTabChange("topplistor")}
            aria-label={t("project.tabLeaderboards")}
            title={t("project.tabLeaderboards")}
            className={`md:hidden btn-ghost p-2 rounded-xl shrink-0 ${
              tab === "topplistor"
                ? "text-[var(--accent)] bg-[var(--accent)]/10 ring-1 ring-[var(--accent)]/35"
                : ""
            }`}
          >
            <Trophy size={18} />
          </button>
          <AccountMenu
            user={user}
            helpReturnTo={`/project/${id}`}
            onLogout={logout}
            testId="project-account-menu"
          />
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu" className="md:hidden btn-ghost p-2"><MoreVertical size={18}/></button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[var(--bg-2)] border-[var(--line-2)] w-72 text-base">
              <SheetHeader><SheetTitle className="text-[var(--accent)] font-display text-2xl">{t("project.menu")}</SheetTitle></SheetHeader>
              <div className="mt-6 flex flex-col gap-2 text-base leading-snug">
                <button data-testid="mobile-tab-settings" onClick={() => { handleTabChange("settings"); setMobileMenuOpen(false); }}
                        className="flex items-center gap-3 px-4 py-3.5 rounded-xl border border-[var(--line-2)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors text-left">
                  <SettingsIcon size={20} className="shrink-0" aria-hidden /> {t("project.rulesMobile")}
                </button>
                <div className="h-px bg-[var(--line)] my-3"/>
                <button onClick={() => { navigator.clipboard.writeText(project.invite_code); toast.success(t("common.copied")); setMobileMenuOpen(false); }}
                        className="flex items-center gap-3 px-4 py-3.5 rounded-xl border border-[var(--line-2)] text-left">
                  <Copy size={18} className="shrink-0" aria-hidden /> {t("project.copyCode")} <span className="font-mono text-[var(--accent)]">{project.invite_code}</span>
                </button>
                <button type="button" onClick={() => { const url = buildInviteLink(project.invite_code); if (url) { navigator.clipboard.writeText(url); toast.success(t("project.toastInviteLinkCopied")); } setMobileMenuOpen(false); }}
                        className="flex items-center gap-3 px-4 py-3.5 rounded-xl border border-[var(--line-2)] text-left">
                  <Copy size={18} className="shrink-0" aria-hidden /> {t("project.copyInviteLink")}
                </button>
                {isSuperuser && (
                  <button onClick={async()=>{await axios.post(`${API}/projects/${id}/send-summary`); toast.success(t("project.toastMailSentShort")); setMobileMenuOpen(false);}}
                          className="flex items-center gap-3 px-4 py-3.5 rounded-xl border border-[var(--line-2)] text-left">
                    <Mail size={18} className="shrink-0" aria-hidden /> {t("project.mailResults")}
                  </button>
                )}
                <button onClick={() => { load(); setMobileMenuOpen(false); }} className="flex items-center gap-3 px-4 py-3.5 rounded-xl border border-[var(--line-2)] text-left">
                  <RefreshCw size={18} className="shrink-0" aria-hidden /> {t("project.refresh")}
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main className="mx-auto max-w-7xl min-w-0 px-0 py-4 sm:px-6 sm:py-8 overflow-x-hidden">
        <Tabs value={tabUi} onValueChange={handleTabChange} className="min-w-0 max-w-full">
          <div className="mb-6 flex w-full min-w-0 justify-center">
            <TabsList className="glass glass-mobile-flat flex h-auto min-h-10 w-full flex-nowrap items-stretch justify-center gap-0 rounded-none border-0 bg-transparent p-0 sm:inline-flex sm:w-auto sm:rounded-none sm:border sm:border-[var(--line)] sm:bg-[var(--panel)]">
              <TabsTrigger data-testid="tab-table" value="table" className="min-w-0 flex-1 rounded-none px-0.5 py-2 text-xs leading-tight shadow-none data-[state=active]:rounded-none data-[state=active]:bg-[var(--accent)] data-[state=active]:text-[var(--accent-foreground)] data-[state=active]:shadow-none sm:flex-none sm:shrink-0 sm:px-4 sm:py-2 sm:text-sm">{t("project.tabOverview")}</TabsTrigger>
              <TabsTrigger data-testid="tab-group" value="group" className="min-w-0 flex-1 rounded-none px-0.5 py-2 text-xs leading-tight shadow-none data-[state=active]:rounded-none data-[state=active]:bg-[var(--accent)] data-[state=active]:text-[var(--accent-foreground)] data-[state=active]:shadow-none sm:flex-none sm:shrink-0 sm:px-4 sm:py-2 sm:text-sm">{t("project.tabGroup")}</TabsTrigger>
              <TabsTrigger data-testid="tab-standings" value="standings" className="min-w-0 flex-1 rounded-none px-0.5 py-2 text-xs leading-tight shadow-none data-[state=active]:rounded-none data-[state=active]:bg-[var(--accent)] data-[state=active]:text-[var(--accent-foreground)] data-[state=active]:shadow-none sm:flex-none sm:shrink-0 sm:px-4 sm:py-2 sm:text-sm">{t("project.tabStandings")}</TabsTrigger>
              <TabsTrigger data-testid="tab-bracket" value="bracket" className="min-w-0 flex-1 rounded-none px-0.5 py-2 text-xs leading-tight shadow-none data-[state=active]:rounded-none data-[state=active]:bg-[var(--accent)] data-[state=active]:text-[var(--accent-foreground)] data-[state=active]:shadow-none sm:flex-none sm:shrink-0 sm:px-4 sm:py-2 sm:text-sm">{t("project.tabBracket")}</TabsTrigger>
              <TabsTrigger data-testid="tab-extras" value="extras" className="min-w-0 flex-1 rounded-none px-0.5 py-2 text-xs leading-tight shadow-none data-[state=active]:rounded-none data-[state=active]:bg-[var(--accent)] data-[state=active]:text-[var(--accent-foreground)] data-[state=active]:shadow-none sm:flex-none sm:shrink-0 sm:px-4 sm:py-2 sm:text-sm">{t("project.tabExtras")}</TabsTrigger>
              <TabsTrigger data-testid="tab-topplistor" value="topplistor" className="hidden min-w-0 shrink-0 rounded-none px-0.5 py-2 text-xs leading-tight shadow-none data-[state=active]:rounded-none data-[state=active]:bg-[var(--accent)] data-[state=active]:text-[var(--accent-foreground)] data-[state=active]:shadow-none md:inline-flex sm:px-4 sm:py-2 sm:text-sm">{t("project.tabLeaderboards")}</TabsTrigger>
              <TabsTrigger data-testid="tab-settings" value="settings" className="hidden min-w-0 shrink-0 rounded-none px-0.5 py-2 text-xs leading-tight shadow-none data-[state=active]:rounded-none data-[state=active]:bg-[var(--accent)] data-[state=active]:text-[var(--accent-foreground)] data-[state=active]:shadow-none md:inline-flex sm:px-4 sm:py-2 sm:text-sm"><SettingsIcon size={14} className="mr-1"/>{t("project.tabSettings")}</TabsTrigger>
            </TabsList>
          </div>
        </Tabs>
        {tab === "group" && (
          <div className="min-w-0 max-w-full overflow-x-hidden">
            <GroupStageList
              oddsByMatchId={project?.odds_by_match_id && typeof project.odds_by_match_id === "object" ? project.odds_by_match_id : {}}
              oddsCsTop3ByMatchId={
                project?.odds_cs_top3_by_match_id && typeof project.odds_cs_top3_by_match_id === "object"
                  ? project.odds_cs_top3_by_match_id
                  : {}
              }
              allFixtures={project.fixtures || []}
              matches={groupMatches}
              bets={bets}
              saveBet={saveMatchBet}
              teamsByCode={teamsByCode}
              logoByCode={logoByCode}
              fillHarryBoy={fillHarryBoy}
              clearAllMatchTips={clearAllMatchTips}
              harryBoyBusy={harryBoyBusy}
              harryBoyProgress={harryBoyProgress}
            />
          </div>
        )}
        {tab === "table" && (
          <FlatTableView
            fixtures={project.fixtures}
            bets={bets}
            saveBet={saveMatchBet}
            teamsByCode={teamsByCode}
            standings={standings}
            bestThirdStandings={bestThirdStandings}
            groups={project.groups}
            logoByCode={logoByCode}
            tournamentKey={project.tournament_key || "WC2026"}
            knockoutUnlockUntil={project?.knockout_tips_unlock_until || null}
          />
        )}
        {tab === "standings" && (
          <StandingsView project={project} standings={standings} groupBets={groupBets}
                         setGroupBets={setGroupBets} teamsByCode={teamsByCode}
                         fixtures={project.fixtures} bets={bets}
                         reload={load} logoByCode={logoByCode}/>
        )}
        {tab === "bracket" && (
          <BracketView
            fixtures={koMatches}
            allFixtures={project.fixtures || []}
            bets={bets}
            saveBet={saveMatchBet}
            teamsByCode={teamsByCode}
            logoByCode={logoByCode}
            oddsByMatchId={project?.odds_by_match_id && typeof project.odds_by_match_id === "object" ? project.odds_by_match_id : {}}
            oddsCsTop3ByMatchId={
              project?.odds_cs_top3_by_match_id && typeof project.odds_cs_top3_by_match_id === "object"
                ? project.odds_cs_top3_by_match_id
                : {}
            }
            knockoutUnlockUntil={project?.knockout_tips_unlock_until || null}
          />
        )}
        {tab === "topplistor" && (
          <LeaderboardHub
            board={leaderboard}
            rules={project.rules}
            project={project}
            picksMatrix={picksMatrix}
            teamsByCode={teamsByCode}
            leaderboardHubBusy={!mockResults && leaderboardHubStatus === "loading"}
          />
        )}
        {tab === "extras" && (
          <ExtrasView project={project} finalsBet={finalsBet} setFinalsBet={setFinalsBet} reload={load}/>
        )}
        {tab === "settings" && (
          <SettingsView project={project} user={user} isOwner={isOwner} isStaff={isStaff} reload={load}/>
        )}
      </main>
    </div>
  );
}

/** CS-kolumner: alltid CS_DISPLAY_ROW_COUNT när 1x2 eller CS finns; pad med – så tabellbredd blir densamma. */
function buildCsDisplayColumns(topScores, oddsLines) {
  const has1x2 = Array.isArray(oddsLines) && oddsLines.length > 0;
  const hasCs = Array.isArray(topScores) && topScores.length > 0;
  if (!has1x2 && !hasCs) return null;
  const cap = CS_DISPLAY_ROW_COUNT;
  const fromData = hasCs ? topScores.slice(0, cap) : [];
  const padLen = Math.max(0, cap - fromData.length);
  const pads = Array.from({ length: padLen }, (_, i) => ({ label: "\u2013", _csPad: true, key: `cs-pad-${i}` }));
  return [...fromData.map((s, i) => ({ ...s, key: `cs-${i}` })), ...pads];
}

const fmt2 = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return "–";
  return n.toFixed(2);
};

const MatchRow = React.memo(function MatchRow({ m, bet, save, teamsByCode, logoByCode = {}, fixtures = [], oddsLines = null, topScores = null }) {
  const { lang, t } = useLocale();
  const locked = isMatchTipInputLocked(m, fixtures);
  const { home, setHome, away, setAway, onFocus, onBlur } = useMatchScoreDraft(bet, save, m.id, locked);
  const loc = lang === "en" ? "en-GB" : "sv-SE";
  const { dateStr, timeStr } = useMemo(() => {
    const ko = kickoffToDate(m.kickoff);
    return {
      dateStr: ko.toLocaleDateString(loc, { day: "numeric", month: "short" }),
      timeStr: ko.toLocaleTimeString(loc, { hour: "2-digit", minute: "2-digit" }),
    };
  }, [m.kickoff, loc]);
  const homeFull = m.home_code ? teamsByCode[m.home_code] : (m.home_placeholder || t("common.tbd"));
  const awayFull = m.away_code ? teamsByCode[m.away_code] : (m.away_placeholder || t("common.tbd"));
  const home_name = formatMatchTeamLabel({
    code: m.home_code,
    placeholder: m.home_code ? null : m.home_placeholder,
    teamName: teamsByCode[m.home_code],
    tbdLabel: t("common.tbd"),
    lang,
  });
  const away_name = formatMatchTeamLabel({
    code: m.away_code,
    placeholder: m.away_code ? null : m.away_placeholder,
    teamName: teamsByCode[m.away_code],
    tbdLabel: t("common.tbd"),
    lang,
  });
  const csColumns = useMemo(() => buildCsDisplayColumns(topScores, oddsLines), [topScores, oddsLines]);
  const displayScore = m.status === "FINISHED" ? formatMatchScore(m, lang) : null;
  const liveScore = effectiveMatchScores(m);

  const scoreCls = `score-input max-sm:!w-10 max-sm:!h-10 max-sm:!text-lg max-sm:!min-w-0 ${locked ? "locked" : ""}`;

  return (
    <div className={`${panelCard} w-full max-w-full min-w-0 px-3 py-4 sm:px-5`} data-testid={`match-row-${m.id}`}>
      <div className="text-center text-[11px] sm:text-xs text-[var(--text-dim)] mb-3 font-mono leading-snug break-words px-0.5">
        {dateStr.toUpperCase()}, {timeStr} · <span className="uppercase">{m.venue}</span>
        {m.status === "LIVE" && <span className="inline-flex items-center gap-1 ml-2 text-danger"><span className="live-dot"/>{t("project.matchLive")}</span>}
        {m.status === "FINISHED" && <span className="ml-2 text-[var(--accent-2)]">{t("project.matchFinished")}</span>}
      </div>
      {/* Grid: fixed-width center so score boxes stay centered; side columns truncate long names (mobile). */}
      <div className="grid w-full max-w-full min-w-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-x-2 sm:gap-x-4 gap-y-1">
        <div className="flex min-w-0 items-center justify-end gap-1.5 sm:gap-3">
          <div className="min-w-0 text-right">
            <div className="truncate font-semibold text-sm sm:text-base inline-flex items-center justify-end gap-1 max-w-full" title={homeFull}>
              <span className="truncate">{home_name}</span>
              <MatchPenWinnerMark match={m} teamCode={m.home_code} className="text-sm shrink-0" />
            </div>
          </div>
          <FlagCircle code={m.home_code} size={36} logoUrl={logoByCode[m.home_code]} />
        </div>
        <div className="flex w-[6.5rem] shrink-0 items-center justify-center gap-1 sm:w-[7.25rem] sm:gap-2">
          <input data-testid={`score-home-${m.id}`} className={scoreCls}
                 type="number" min="0" value={home} disabled={locked}
                 onChange={e=>setHome(e.target.value)} onFocus={onFocus} onBlur={onBlur}/>
          <span className="shrink-0 text-[var(--text-dim)]">:</span>
          <input data-testid={`score-away-${m.id}`} className={scoreCls}
                 type="number" min="0" value={away} disabled={locked}
                 onChange={e=>setAway(e.target.value)} onFocus={onFocus} onBlur={onBlur}/>
        </div>
        <div className="flex min-w-0 items-center justify-start gap-1.5 sm:gap-3">
          <FlagCircle code={m.away_code} size={36} logoUrl={logoByCode[m.away_code]} />
          <div className="min-w-0 text-left">
            <div className="truncate font-semibold text-sm sm:text-base inline-flex items-center gap-1 max-w-full" title={awayFull}>
              <MatchPenWinnerMark match={m} teamCode={m.away_code} className="text-sm shrink-0" />
              <span className="truncate">{away_name}</span>
            </div>
          </div>
        </div>
      </div>
      {m.status === "FINISHED" && displayScore && (
        <div className="text-center mt-3 text-xs font-mono">
          <span className="text-[var(--text-dim)]">{t("project.resultLabel")} </span>
          <span className="text-[var(--accent-2)] font-bold tracking-wide">{displayScore}</span>
        </div>
      )}
      {m.status === "LIVE" && liveScore && (
        <div className="text-center mt-3 text-xs font-mono">
          <span className="text-[var(--text-dim)]">{t("project.resultLabel")} </span>
          <span className="text-[var(--accent-2)] font-bold tracking-wide">{liveScore.home}–{liveScore.away}</span>
        </div>
      )}
      {csColumns && (
        <div className="mt-3 pt-3 border-t border-[var(--line)] border-dashed">
          <details className="group" data-testid={`match-odds-${m.id}`}>
            <summary className="cursor-pointer select-none list-none">
              <div className="text-[10px] uppercase tracking-wider text-[var(--text-dim)] mb-2 text-center group-open:mb-2">
                {t("project.oddsCaption")}
                <span className="ml-2 text-[9px] normal-case tracking-normal text-[var(--text-dim)] opacity-80">
                  (tryck för att visa)
                </span>
              </div>
            </summary>
            <div className="w-full overflow-x-auto sm:flex sm:justify-center">
            <table className="w-full max-w-full text-[10px] sm:text-xs font-mono table-fixed sm:max-w-xl">
              <thead>
                <tr className="text-[var(--text-dim)]">
                  <th className="text-left font-normal pb-1 pr-1 w-[30%] sm:w-auto min-w-0">{t("project.oddsBook")}</th>
                  <th colSpan={3} className="pb-1 pl-0.5 pr-0 font-normal sm:pl-8 sm:pr-2 md:pl-10">
                    <div className="flex w-full justify-center font-mono tabular-nums">
                      <span className="inline-flex items-center justify-center gap-[0.4rem] sm:gap-2.5">
                        <span className="min-w-[1.6rem] text-center sm:min-w-[2rem]">1</span>
                        <span className="min-w-[1.6rem] text-center sm:min-w-[2rem]">X</span>
                        <span className="min-w-[1.6rem] text-center sm:min-w-[2rem]">2</span>
                      </span>
                    </div>
                  </th>
                  {csColumns.map((s, i) => (
                    <th
                      key={s.key}
                      className={`text-center font-normal pb-1 w-[2.05rem] sm:w-11 tabular-nums leading-tight shrink-0 ${
                        i === 0 ? "sm:border-l-0" : ""
                      }`}
                    >
                      <span
                        className="inline-block max-w-[2.75rem] truncate sm:max-w-none"
                        title={s._csPad ? undefined : s.label}
                      >
                        {s._csPad ? "\u2013" : s.label}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(oddsLines || []).map((row) => (
                  <tr key={row.key} className="border-t border-[var(--line)]/60">
                    <td className="py-1.5 pr-1 text-[var(--text-dim)] truncate max-w-[7rem] sm:max-w-none min-w-0" title={row.title}>
                      {row.title}
                    </td>
                    <td colSpan={3} className="py-1.5 pl-0.5 pr-0 font-mono tabular-nums sm:pl-8 sm:pr-2 md:pl-10">
                      <div className="flex w-full justify-center">
                        <span className="inline-flex items-center justify-center gap-[0.4rem] tabular-nums sm:gap-2.5">
                          <span className="min-w-[1.6rem] text-center text-[var(--accent)] sm:min-w-[2rem]">
                            {fmt2(row.home)}
                          </span>
                          <span className="min-w-[1.6rem] text-center sm:min-w-[2rem]">
                            {fmt2(row.draw)}
                          </span>
                          <span className="min-w-[1.6rem] text-center text-[var(--accent)] sm:min-w-[2rem]">
                            {fmt2(row.away)}
                          </span>
                        </span>
                      </div>
                    </td>
                    {csColumns.map((sc, i) => {
                      let v = null;
                      if (!sc._csPad) {
                        if (row.key === "svenskaspel_se" && (sc.svenskaspel_se != null || sc.bet365 != null))
                          v = sc.svenskaspel_se ?? sc.bet365;
                        else if (row.key === "unibet_se" && sc.unibet_se != null) v = sc.unibet_se;
                      }
                      return (
                        <td
                          key={`cs-d-${row.key}-${sc.key}`}
                          className={`py-1.5 text-center tabular-nums text-[var(--text-dim)] w-[2.05rem] sm:w-11 shrink-0 ${
                            i === 0 ? "sm:border-l-0" : ""
                          }`}
                        >
                          {v != null ? fmt2(v) : "–"}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr className="border-t-2 border-[var(--line)] bg-[var(--panel)]/40">
                  <td colSpan={4} className="py-1.5 pr-1 text-[var(--text-dim)] text-left">
                    {t("project.oddsAvgCsCaption")}
                  </td>
                  {csColumns.map((s, i) => (
                    <td
                      key={`cs-f-${s.key}`}
                      className={`py-1.5 text-center tabular-nums text-[var(--accent)] font-medium w-[2.05rem] sm:w-11 shrink-0 ${
                        i === 0 ? "sm:border-l-0" : ""
                      }`}
                    >
                      {s._csPad ? "–" : s.avg != null && s.avg !== "" ? fmt2(s.avg) : "–"}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
            </div>
          </details>
        </div>
      )}
    </div>
  );
}, (prev, next) => {
  // Cheap comparisons to avoid re-rendering hundreds of rows on unrelated state changes.
  if (prev.m !== next.m) return false;
  const pb = prev.bet || {};
  const nb = next.bet || {};
  if (pb.home_score !== nb.home_score) return false;
  if (pb.away_score !== nb.away_score) return false;
  if (prev.oddsLines !== next.oddsLines) return false;
  if (prev.topScores !== next.topScores) return false;
  if (prev.teamsByCode !== next.teamsByCode) return false;
  if (prev.logoByCode !== next.logoByCode) return false;
  if (prev.fixtures !== next.fixtures) return false;
  return true;
});

function GroupStageList({
  oddsByMatchId = {},
  oddsCsTop3ByMatchId = {},
  allFixtures = [],
  matches,
  bets,
  saveBet,
  teamsByCode,
  logoByCode = {},
  fillHarryBoy,
  clearAllMatchTips,
  harryBoyBusy = false,
  harryBoyProgress = null,
}) {
  const { lang, t } = useLocale();
  const loc = lang === "en" ? "en-GB" : "sv-SE";
  const dateSections = useMemo(() => {
    const sorted = [...matches].sort(byKickoffAsc);
    const sections = [];
    let label = null;
    let bucket = [];
    for (const m of sorted) {
      const d = kickoffToDate(m.kickoff).toLocaleDateString(loc, { weekday: "long", day: "numeric", month: "short" });
      if (d !== label) {
        if (bucket.length) sections.push({ label, matches: bucket });
        label = d;
        bucket = [m];
      } else {
        bucket.push(m);
      }
    }
    if (bucket.length) sections.push({ label, matches: bucket });
    return sections;
  }, [matches, loc]);
  const total = matches.length;
  const filled = matches.filter((m) => {
    const b = bets[String(m.id ?? "")] ?? bets[m.id];
    return b && b.home_score != null && b.away_score != null;
  }).length;

  return (
    <div className="mx-auto w-full min-w-0 max-w-full space-y-8 sm:max-w-3xl">
      <div className="text-center px-3 sm:px-0">
        <h2 className={TAB_MAIN_TITLE_CLASS}>{t("project.tabGroup")}</h2>
        <div className="mt-3 h-1 bg-[var(--panel)] rounded-full overflow-hidden">
          <div className="h-full bg-[var(--accent)]" style={{ width: `${total ? (filled / total) * 100 : 0}%` }}/>
        </div>
        <div className="text-xs mt-2 text-[var(--text-dim)] font-mono">{t("project.groupProgress", { filled, total })}</div>
      </div>
      {typeof fillHarryBoy === "function" && (
        <div className="mx-0 border-0 bg-transparent px-3 py-4 sm:mx-0 sm:rounded-2xl sm:border sm:border-[var(--line)] sm:bg-[var(--panel)]/55 sm:px-5 sm:py-5">
          <div className="flex items-center justify-center gap-2.5 text-[var(--accent)] mb-2">
            <Sparkles size={22} className="shrink-0" aria-hidden />
            <span className="text-lg sm:text-xl font-display tracking-wide">{t("project.harryBoyTitle")}</span>
          </div>
          <p className="text-sm sm:text-base text-center text-[var(--text-dim)] mb-4 leading-relaxed max-w-xl mx-auto">{t("project.harryBoyHint")}</p>
          <div className="flex flex-wrap justify-center gap-2.5 sm:gap-3">
            <button
              type="button"
              disabled={harryBoyBusy}
              title={t("project.harryBoyTightTitle")}
              onClick={() => fillHarryBoy("tight")}
              className="btn-ghost text-sm sm:text-base px-4 py-2.5 sm:px-5 sm:py-3 rounded-xl border border-[var(--line)] hover:border-[var(--accent)] disabled:opacity-50"
            >
              {t("project.harryBoyTight")}
            </button>
            <button
              type="button"
              disabled={harryBoyBusy}
              title={t("project.harryBoyRichTitle")}
              onClick={() => fillHarryBoy("rich")}
              className="btn-ghost text-sm sm:text-base px-4 py-2.5 sm:px-5 sm:py-3 rounded-xl border border-[var(--line)] hover:border-[var(--accent)] disabled:opacity-50"
            >
              {t("project.harryBoyRich")}
            </button>
            <button
              type="button"
              disabled={harryBoyBusy}
              title={t("project.harryBoySafeTitle")}
              onClick={() => fillHarryBoy("safe")}
              className="btn-ghost text-sm sm:text-base px-4 py-2.5 sm:px-5 sm:py-3 rounded-xl border border-[var(--line)] hover:border-[var(--accent)] disabled:opacity-50"
            >
              {t("project.harryBoySafe")}
            </button>
            <button
              type="button"
              disabled={harryBoyBusy}
              title={t("project.harryBoyMixTitle")}
              onClick={() => fillHarryBoy("mix")}
              className="btn-ghost text-sm sm:text-base px-4 py-2.5 sm:px-5 sm:py-3 rounded-xl border border-[var(--line)] hover:border-[var(--accent)] disabled:opacity-50"
            >
              {t("project.harryBoyMix")}
            </button>
            {typeof clearAllMatchTips === "function" && (
              <button
                type="button"
                disabled={harryBoyBusy}
                title={t("project.clearAllTitle")}
                onClick={clearAllMatchTips}
                className="btn-ghost btn-destructive text-sm sm:text-base px-4 py-2.5 sm:px-5 sm:py-3 rounded-xl disabled:opacity-50"
              >
                {t("project.clearAll")}
              </button>
            )}
          </div>
          {harryBoyBusy && (
            <div className="mt-4 space-y-1.5" aria-live="polite">
              <div className="flex items-center justify-center gap-2 text-sm text-[var(--text-dim)]">
                <Loader2 className="h-4 w-4 animate-spin shrink-0 text-[var(--accent)]" aria-hidden />
                <span>
                  {harryBoyProgress && harryBoyProgress.total > 0
                    ? t("project.harryBoyProgress", { done: harryBoyProgress.done, total: harryBoyProgress.total })
                    : t("project.harryBoyPreparing")}
                </span>
              </div>
              {harryBoyProgress && harryBoyProgress.total > 0 && (
                <div className="h-1.5 rounded-full bg-[var(--panel)] overflow-hidden">
                  <div
                    className="h-full bg-[var(--accent)] transition-[width] duration-150 ease-out"
                    style={{ width: `${(harryBoyProgress.done / harryBoyProgress.total) * 100}%` }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {dateSections.map(({ label, matches: ms }, dayIdx) => (
        <div key={label} className="min-w-0">
          {dayIdx > 0 && (
            <div
              className="mb-8 mt-4 h-1.5 w-full bg-[var(--accent)]"
              role="separator"
              aria-hidden
            />
          )}
          <div className="text-center text-[var(--text-dim)] tracking-widest uppercase text-sm sm:text-base mb-3 px-3 sm:px-0">
            {label}
          </div>
          <div className="flex flex-col gap-3 max-sm:gap-7">
            {ms.map(m => {
              const matchKey = String(m.id ?? "");
              const topCsRaw = oddsCsTop3ByMatchId[matchKey] ?? oddsCsTop3ByMatchId[m.id] ?? null;
              const topScoresForTable =
                Array.isArray(topCsRaw) && topCsRaw.length > 0 ? topCsRaw.slice(0, CS_DISPLAY_ROW_COUNT) : null;
              return (
                <MatchRow
                  key={matchKey}
                  m={m}
                  bet={bets[matchKey] ?? bets[m.id]}
                  save={saveBet}
                  teamsByCode={teamsByCode}
                  logoByCode={logoByCode}
                  fixtures={allFixtures}
                  oddsLines={oddsByMatchId[matchKey] ?? oddsByMatchId[m.id] ?? null}
                  topScores={topScoresForTable}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function FlatTableView({
  fixtures,
  bets,
  saveBet,
  teamsByCode,
  standings,
  bestThirdStandings = [],
  groups,
  logoByCode = {},
  tournamentKey = "WC2026",
  knockoutUnlockUntil = null,
}) {
  const { lang, t } = useLocale();
  const [mode, setMode] = useState("tipped"); // 'live' | 'tipped'
  const [matchSearch, setMatchSearch] = useState("");
  const fixturesChrono = useMemo(() => [...fixtures].sort(byKickoffAsc), [fixtures]);
  const searchQuery = useMemo(() => normalizeOverviewSearchQuery(matchSearch), [matchSearch]);
  const visibleFixtures = useMemo(() => {
    if (searchQuery.length < 2) return fixturesChrono;
    return fixturesChrono.filter((m) =>
      fixtureMatchesOverviewQuery(m, searchQuery, teamsByCode),
    );
  }, [fixturesChrono, searchQuery, teamsByCode]);
  // Compute tipped standings from user's match bets
  const tippedStandings = React.useMemo(
    () => calcGroupStandingsFromFixtures(fixtures, groups, bets),
    [fixtures, bets, groups],
  );

  // Live teams: codes currently playing
  const liveCodes = new Set(fixtures.filter(m => m.status === "LIVE").flatMap(m => [m.home_code, m.away_code].filter(Boolean)));

  const tableData = mode === "live" ? standings : tippedStandings;
  const showWcTvCol = tournamentKey === "WC2026";

  return (
    <div className="flex flex-col gap-0 lg:grid lg:grid-cols-[1fr,360px] lg:gap-6 max-sm:divide-y max-sm:divide-[var(--line)] min-w-0 max-w-full overflow-x-hidden">
      <div className={`${panelCardOverflow} min-w-0 max-w-full`}>
        <div className="px-3 pt-3 pb-2 sm:px-4 border-b border-[var(--line)]">
          <Input
            type="search"
            data-testid="overview-match-search"
            value={matchSearch}
            onChange={(e) => setMatchSearch(e.target.value)}
            placeholder={t("flat.searchPlaceholder")}
            className="bg-[var(--bg-2)] border-[var(--line-2)] text-sm"
            aria-label={t("flat.searchPlaceholder")}
          />
        </div>
        <table className="w-full min-w-0 max-w-full text-sm md:table-fixed">
          <colgroup className="hidden md:table-column-group">
            <col className="w-[5rem]" />
            <col className="w-[3rem]" />
            <col />
            <col className="w-[6.125rem]" />
            <col className="w-[4.75rem]" />
            <col className="w-[7.25rem]" />
            {showWcTvCol ? <col className="w-[4.25rem]" /> : null}
          </colgroup>
          <thead className="hidden md:table-header-group text-[var(--text-dim)] uppercase text-xs">
            <tr className="border-b border-[var(--line)]">
              <th className="text-left py-3 pl-3 pr-1 whitespace-nowrap w-[5rem] max-w-[5rem] box-border">{t("flat.colDate")}</th>
              <th className="text-left py-3 pl-1 pr-2 whitespace-nowrap w-[3rem] max-w-[3rem] box-border">{t("flat.colTime")}</th>
              <th className="text-left p-3 min-w-0">{t("flat.colMatch")}</th>
              <th className="text-center p-3 pl-3 whitespace-nowrap w-[6.125rem]">{t("flat.colTips")}</th>
              <th className="text-center p-3 pl-3 whitespace-nowrap w-[4.75rem]">{t("flat.colResult")}</th>
              <th className="text-center p-3 whitespace-nowrap w-[7.25rem] max-w-[7.25rem] box-border">{t("flat.colRound")}</th>
              {showWcTvCol && (
                <th className="text-center py-3 px-2 whitespace-nowrap w-[4.25rem] align-middle box-border">{t("flat.colTv")}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {visibleFixtures.length === 0 ? (
              <tr>
                <td
                  colSpan={showWcTvCol ? 7 : 6}
                  className="p-6 text-center text-sm text-[var(--text-dim)]"
                >
                  {t("flat.searchNoMatches")}
                </td>
              </tr>
            ) : (
              visibleFixtures.map((m) => (
                <FlatRow
                  key={m.id}
                  m={m}
                  bet={bets[m.id]}
                  save={saveBet}
                  teamsByCode={teamsByCode}
                  lang={lang}
                  t={t}
                  fixtures={fixtures}
                  logoByCode={logoByCode}
                  tournamentKey={tournamentKey}
                  knockoutUnlockUntil={knockoutUnlockUntil}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      <div>
        <div className="flex flex-col items-center gap-4 mb-4 px-3 sm:px-1">
          <div className="font-display text-2xl w-full text-center">{t("flat.title")}</div>
          <div className="flex w-full max-w-md rounded-none overflow-hidden border border-[var(--line-2)] divide-x divide-[var(--line-2)]">
            <button
              type="button"
              data-testid="standings-toggle-tipped"
              onClick={() => setMode("tipped")}
              className={`flex-1 px-5 py-3 text-base font-medium rounded-none border-0 ${
                mode === "tipped" ? "tab-active" : "bg-[var(--panel)]/50 text-[var(--text-dim)]"
              }`}
            >
              {t("flat.tipped")}
            </button>
            <button
              type="button"
              data-testid="standings-toggle-live"
              onClick={() => setMode("live")}
              className={`flex-1 px-5 py-3 text-base font-medium rounded-none border-0 ${
                mode === "live" ? "tab-active" : "bg-[var(--panel)]/50 text-[var(--text-dim)]"
              }`}
            >
              {t("flat.live")}
            </button>
          </div>
        </div>
        <div className="flex flex-col gap-4 max-sm:gap-0 max-sm:divide-y max-sm:divide-[var(--line)]">
          {Object.entries(tableData).map(([g, rows]) => (
            <div key={g} className={`${panelCard} p-3 sm:p-4`}>
              <div className="font-display text-2xl mb-2">{t("flat.groupPrefix")} {g}</div>
              <table className="w-full text-xs font-mono table-fixed">
                <thead className="text-[var(--text-dim)]">
                  <tr>
                    <th className="text-left w-[52%] sm:w-auto">{t("flat.teamCol")}</th>
                    <StandingsStatHeaderCells t={t} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.code} className="border-t border-[var(--line)]">
                      <td className="py-1.5 min-w-0">
                        <div className="flex min-w-0 items-center gap-2">
                        <FlagCircle code={r.code} size={20} logoUrl={logoByCode[r.code]}/>
                        <span className="min-w-0 truncate">{teamsByCode[r.code] || r.code}</span>
                        {mode === "live" && liveCodes.has(r.code) && <span className="text-danger" title={t("flat.playingNow")}>*</span>}
                        </div>
                      </td>
                      <StandingsStatBodyCells row={r} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          {bestThirdStandings.length > 0 && (
            <div className={`${panelCard} p-3 sm:p-4`}>
              <div className="font-display text-2xl mb-2">{t("flat.bestThirdTitle")}</div>
              <table className="w-full text-xs font-mono table-fixed">
                <thead className="text-[var(--text-dim)]">
                  <tr>
                    <th className="text-left w-[52%] sm:w-auto">{t("flat.teamCol")}</th>
                    <StandingsStatHeaderCells t={t} />
                  </tr>
                </thead>
                <tbody>
                  {bestThirdStandings.map((r, idx) => (
                    <tr key={r.code} className={bestThirdRowClassName(idx, bestThirdStandings.length)}>
                      <td className="py-1.5 min-w-0">
                        <div className="flex min-w-0 items-center gap-2">
                          <FlagCircle code={r.code} size={20} logoUrl={logoByCode[r.code]} />
                          <span className="min-w-0 truncate">{teamsByCode[r.code] || r.code}</span>
                          {liveCodes.has(r.code) && <span className="text-danger" title={t("flat.playingNow")}>*</span>}
                        </div>
                      </td>
                      <StandingsStatBodyCells row={r} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {mode === "live" && (
          <p className="text-xs text-[var(--text-dim)] mt-2 px-3 sm:px-1">{t("project.liveTeamsHint")}</p>
        )}
      </div>
    </div>
  );
}

function FlatRow({ m, bet, save, teamsByCode, lang, t, logoByCode = {}, tournamentKey = "WC2026", fixtures = [], knockoutUnlockUntil = null }) {
  const loc = lang === "en" ? "en-GB" : "sv-SE";
  const locked = isMatchTipInputLocked(m, fixtures, knockoutUnlockUntil);
  const { home: h, setHome: sh, away: a, setAway: sa, onFocus, onBlur } = useMatchScoreDraft(
    bet,
    save,
    m.id,
    locked,
  );
  const ko = kickoffToDate(m.kickoff);
  const dateStr = ko.toLocaleDateString(loc, { day: "numeric", month: "short" });
  const timeStr = ko.toLocaleTimeString(loc, { hour: "2-digit", minute: "2-digit" });
  const homeFull = m.home_code ? teamsByCode[m.home_code] : (m.home_placeholder || t("common.tbd"));
  const awayFull = m.away_code ? teamsByCode[m.away_code] : (m.away_placeholder || t("common.tbd"));
  const homeName = formatMatchTeamLabel({
    code: m.home_code,
    placeholder: m.home_code ? null : m.home_placeholder,
    teamName: teamsByCode[m.home_code],
    tbdLabel: t("common.tbd"),
    lang,
  });
  const awayName = formatMatchTeamLabel({
    code: m.away_code,
    placeholder: m.away_code ? null : m.away_placeholder,
    teamName: teamsByCode[m.away_code],
    tbdLabel: t("common.tbd"),
    lang,
  });
  const grpOrRound = matchRoundDisplay(m, t);
  const metaSmall = "text-[11px] leading-snug text-[var(--text-dim)] font-mono uppercase tracking-wide";
  const liveScore = effectiveMatchScores(m);
  const resultBlock =
    (m.status === "FINISHED" || m.status === "LIVE") && liveScore ? (
      <span className={`font-semibold tabular-nums ${m.status === "LIVE" ? "text-danger" : "text-[var(--accent-2)]"}`}>
        {liveScore.home}–{liveScore.away}
      </span>
    ) : m.status === "LIVE" ? (
      <span className="text-danger inline-flex items-center gap-1">
        <span className="live-dot" />{t("project.matchLive")}
      </span>
    ) : (
      <span>–</span>
    );

  const wcTv =
    tournamentKey === "WC2026"
      ? wc2026BroadcasterSweden(m.stage, m.home_code, m.away_code, m.match_no)
      : null;

  return (
    <>
      <tr className="md:hidden border-b border-[var(--line)]">
        <td colSpan={tournamentKey === "WC2026" ? 7 : 6} className="p-4 align-top">
          <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 items-start">
            <div className="flex w-full min-w-0 flex-col space-y-2">
              <div className={metaSmall + " w-full text-left"}>
                <span>{dateStr.toUpperCase()}, {timeStr}</span>
                <span>{' · '}{grpOrRound}</span>
              </div>
              <div className="flex w-full min-w-0 items-center gap-1.5 overflow-hidden text-left text-sm font-semibold">
                <span className="shrink-0">
                  <FlagCircle code={m.home_code} size={22} logoUrl={logoByCode[m.home_code]} />
                </span>
                <span className="min-w-0 max-w-[42%] shrink truncate" title={homeFull}>{homeName}</span>
                <span className="text-[var(--text-dim)] shrink-0">–</span>
                <span className="min-w-0 max-w-[52%] shrink truncate" title={awayFull}>{awayName}</span>
                <span className="shrink-0">
                  <FlagCircle code={m.away_code} size={22} logoUrl={logoByCode[m.away_code]} />
                </span>
              </div>
              {/* Mobile: hide TV logos (perf test). */}
            </div>
              <div className="flex flex-col items-center gap-1.5 shrink-0 min-w-[6.25rem]">
              <span className={metaSmall + " uppercase text-[10px] opacity-80 text-center w-full"}>{t("flat.colTips")}</span>
              <div className="flex items-center gap-1 justify-center w-full">
                <input
                  data-testid={`flat-home-${m.id}`}
                  disabled={locked}
                  type="number"
                  min="0"
                  value={h}
                  onChange={(e) => sh(e.target.value)}
                  onFocus={onFocus}
                  onBlur={onBlur}
                  className={`score-input ${locked ? "locked" : ""}`}
                  style={{ width: 40, height: 34, fontSize: 15 }}
                />
                <span className="text-[var(--text-dim)] text-sm">:</span>
                <input
                  data-testid={`flat-away-${m.id}`}
                  disabled={locked}
                  type="number"
                  min="0"
                  value={a}
                  onChange={(e) => sa(e.target.value)}
                  onFocus={onFocus}
                  onBlur={onBlur}
                  className={`score-input ${locked ? "locked" : ""}`}
                  style={{ width: 40, height: 34, fontSize: 15 }}
                />
              </div>
              <div className="text-center w-full text-[11px] leading-snug font-mono text-[var(--text-dim)] space-y-0.5">
                <div className="uppercase text-[10px] opacity-80">{t("flat.colResult")}</div>
                <div className="text-sm font-semibold tabular-nums pt-px">{resultBlock}</div>
              </div>
            </div>
          </div>
        </td>
      </tr>
      <tr className="hidden md:table-row border-b border-[var(--line)] hover:bg-[var(--panel)]/50">
        <td className="py-3 pl-3 pr-1 font-mono text-xs whitespace-nowrap align-top w-[5rem] max-w-[5rem] box-border">
          {ko.toLocaleDateString(loc, { day: "numeric", month: "short" })}
        </td>
        <td className="py-3 pl-1 pr-2 font-mono text-xs whitespace-nowrap align-top w-[3rem] max-w-[3rem] box-border">
          {ko.toLocaleTimeString(loc, { hour: "2-digit", minute: "2-digit" })}
        </td>
        <td className="p-3 min-w-0 align-top">
          <div className="flex flex-nowrap items-center gap-1.5 min-w-0 overflow-x-auto no-scrollbar">
            <span className="shrink-0"><FlagCircle code={m.home_code} size={20} logoUrl={logoByCode[m.home_code]} /></span>
            <span className="text-right shrink-0" title={homeFull}>{homeName}</span>
            <span className="text-[var(--text-dim)] mx-0.5 shrink-0">{t("flat.vs")}</span>
            <span className="shrink-0" title={awayFull}>{awayName}</span>
            <span className="shrink-0"><FlagCircle code={m.away_code} size={20} logoUrl={logoByCode[m.away_code]} /></span>
          </div>
        </td>
        <td className="p-3 pl-3 text-center">
          <div className="flex items-center gap-1 justify-center">
            <input
              data-testid={`flat-home-${m.id}`}
              disabled={locked}
              type="number"
              min="0"
              value={h}
              onChange={(e) => sh(e.target.value)}
              onFocus={onFocus}
              onBlur={onBlur}
              className={`score-input ${locked ? "locked" : ""}`}
              style={{ width: 36, height: 32, fontSize: 14 }}
            />
            <span className="text-[var(--text-dim)]">:</span>
            <input
              data-testid={`flat-away-${m.id}`}
              disabled={locked}
              type="number"
              min="0"
              value={a}
              onChange={(e) => sa(e.target.value)}
              onFocus={onFocus}
              onBlur={onBlur}
              className={`score-input ${locked ? "locked" : ""}`}
              style={{ width: 36, height: 32, fontSize: 14 }}
            />
          </div>
        </td>
        <td className="p-3 pl-3 text-center font-mono text-xs w-[4.75rem] max-w-[4.75rem] box-border">
          {resultBlock}
        </td>
        <td className="p-3 text-center w-[7.25rem] max-w-[7.25rem] box-border align-top">
          <span className="inline-block max-w-full truncate text-center text-xs px-2 py-0.5 rounded-full border border-[var(--line-2)]">
            {grpOrRound}
          </span>
        </td>
        {tournamentKey === "WC2026" && (
          <td className="py-3 px-2 text-center align-middle whitespace-nowrap w-[4.25rem] max-w-[4.25rem] min-w-0 box-border">
            <div className="flex min-w-0 justify-center items-center min-h-[1.5rem]">
              <WcBroadcastCell channel={wcTv} />
            </div>
          </td>
        )}
      </tr>
    </>
  );
}

function StandingsView({ project, standings, groupBets, setGroupBets, teamsByCode, reload, fixtures, bets, logoByCode = {} }) {
  const { lang, t } = useLocale();
  const [mode, setMode] = useState("tipped");
  const groups = Object.keys(project.groups).sort();
  const groupMatchesByLetter = useMemo(() => {
    const out = Object.fromEntries(groups.map((g) => [g, []]));
    for (const m of fixtures || []) {
      if (m.stage === "GROUP" && m.group && out[m.group]) out[m.group].push(m);
    }
    for (const g of groups) out[g].sort(byKickoffAsc);
    return out;
  }, [fixtures, groups]);
  const totalPlatser = groups.length * 3;
  const ifylldaPlatser = groups.reduce((n, g) => {
    const gb = groupBets[g] || {};
    return n + (gb.first ? 1 : 0) + (gb.second ? 1 : 0) + (gb.third ? 1 : 0);
  }, 0);
  const heltIfylldaGrupper = groups.filter(g => {
    const gb = groupBets[g] || {};
    return gb.first && gb.second && gb.third;
  }).length;
  const tippedStandings = React.useMemo(
    () => calcGroupStandingsFromFixtures(fixtures, project.groups, bets),
    [fixtures, bets, project.groups],
  );
  const liveCodes = new Set(fixtures.filter(m => m.status === "LIVE").flatMap(m => [m.home_code, m.away_code].filter(Boolean)));
  const tableData = mode === "live" ? standings : tippedStandings;
  const groupLocked = isGroupPhaseLocked(fixtures || []);
  const [fillBusy, setFillBusy] = useState(false);
  const [clearBusy, setClearBusy] = useState(false);

  const clearStandingsPlacements = useCallback(async () => {
    if (groupLocked) {
      toast.error(t("project.harryBoyGroupLocked"));
      return;
    }
    if (clearBusy || fillBusy) return;
    if (ifylldaPlatser === 0) {
      toast.error(t("standings.clearPlacementsNone"));
      return;
    }
    if (!window.confirm(t("standings.clearPlacementsConfirm"))) return;
    setClearBusy(true);
    try {
      const payload = groups.map((g) => ({
        group: g,
        first: "",
        second: "",
        third: null,
      }));
      await axios.post(`${API}/projects/${project.id}/bets/groups`, payload);
      const cleared = Object.fromEntries(groups.map((g) => [g, { group: g }]));
      setGroupBets(cleared);
      toast.success(t("standings.clearPlacementsToast"));
      if (reload) await reload();
    } catch (e) {
      toast.error(formatApiErrorMessage(e, t("standings.err")) || t("standings.err"));
    } finally {
      setClearBusy(false);
    }
  }, [groupLocked, clearBusy, fillBusy, ifylldaPlatser, groups, project.id, setGroupBets, reload, t]);

  const fillFromMatchTips = useCallback(async () => {
    if (groupLocked) {
      toast.error(t("project.harryBoyGroupLocked"));
      return;
    }
    if (fillBusy) return;
    setFillBusy(true);
    try {
      const nextBets = { ...groupBets };
      const payload = [];
      let filled = 0;
      for (const g of groups) {
        const cur = groupBets[g] || { group: g };
        const tippedOrder = (tippedStandings[g] || []).map((r) => r.code).filter(Boolean);
        if (!tippedOrder.length) continue;
        const used = new Set([cur.first, cur.second, cur.third].filter(Boolean));
        const updated = { ...cur, group: g };
        let changed = false;
        for (const field of ["first", "second", "third"]) {
          if (updated[field]) continue;
          const pick = tippedOrder.find((code) => !used.has(code));
          if (!pick) break;
          updated[field] = pick;
          used.add(pick);
          filled += 1;
          changed = true;
        }
        if (changed) {
          nextBets[g] = updated;
          payload.push({
            group: g,
            first: updated.first || "",
            second: updated.second || "",
            third: updated.third || null,
          });
        }
      }
      if (filled === 0) {
        toast.error(t("standings.fillFromMatchTipsNone"));
        return;
      }
      await axios.post(`${API}/projects/${project.id}/bets/groups`, payload);
      setGroupBets(nextBets);
      toast.success(t("standings.fillFromMatchTipsToast", { n: filled }));
      if (reload) await reload();
    } catch (e) {
      toast.error(formatApiErrorMessage(e, t("standings.err")) || t("standings.err"));
    } finally {
      setFillBusy(false);
    }
  }, [groupLocked, fillBusy, groupBets, groups, tippedStandings, project.id, setGroupBets, reload, t]);

  const updatePred = async (g, field, code) => {
    if (groupLocked) {
      toast.error(t("project.harryBoyGroupLocked"));
      return;
    }
    const cur = groupBets[g] || { group: g };
    const updated = { ...cur, [field]: code };
    setGroupBets({ ...groupBets, [g]: updated });
    try {
      await axios.post(`${API}/projects/${project.id}/bets/groups`, [{
        group: g, first: updated.first || "", second: updated.second || "", third: updated.third || null
      }]);
      toast.success(t("standings.saved", { g }));
    } catch { toast.error(t("standings.err")); }
  };
  return (
    <div>
      <div className="text-center w-full px-3 sm:px-0 sm:max-w-md sm:mx-auto mb-8">
        <h2 className={`${TAB_MAIN_TITLE_CLASS} mb-2`}>{t("standings.title")}</h2>
        <div className="mt-3 h-1.5 bg-[var(--panel)] rounded-full overflow-hidden">
          <div
            className="h-full bg-[var(--accent)] transition-[width] duration-300"
            style={{ width: totalPlatser ? `${(ifylldaPlatser / totalPlatser) * 100}%` : "0%" }}
          />
        </div>
        <div className="text-xs mt-2 text-[var(--text-dim)] font-mono">
          {t("standings.progressSlots", { filled: ifylldaPlatser, total: totalPlatser })}
        </div>
        {heltIfylldaGrupper < groups.length && (
          <p className="text-xs text-[var(--text-dim)] mt-1">
            {t("standings.groupsDoneLine", { done: heltIfylldaGrupper, total: groups.length })}
          </p>
        )}
        {groupLocked && (
          <p className="text-sm text-[var(--text-dim)] mt-3">{t("project.groupPhaseLockedHint")}</p>
        )}
      </div>
      <div className="flex justify-center mb-6 px-3 sm:px-0">
        <div className="flex w-full max-w-md rounded-none overflow-hidden border border-[var(--line-2)] divide-x divide-[var(--line-2)]">
          <button
            type="button"
            data-testid="standings-page-toggle-tipped"
            onClick={() => setMode("tipped")}
            className={`flex-1 px-5 py-3 text-base font-medium rounded-none border-0 ${
              mode === "tipped" ? "tab-active" : "bg-[var(--panel)]/50 text-[var(--text-dim)]"
            }`}
          >
            {t("flat.tipped")}
          </button>
          <button
            type="button"
            data-testid="standings-page-toggle-live"
            onClick={() => setMode("live")}
            className={`flex-1 px-5 py-3 text-base font-medium rounded-none border-0 ${
              mode === "live" ? "tab-active" : "bg-[var(--panel)]/50 text-[var(--text-dim)]"
            }`}
          >
            {t("flat.live")}
          </button>
        </div>
      </div>
      <div className="flex flex-wrap justify-center gap-3 mb-6 px-3 sm:px-0">
        <button
          type="button"
          data-testid="btn-fill-standings-from-tips"
          onClick={fillFromMatchTips}
          disabled={groupLocked || fillBusy || clearBusy}
          title={t("standings.fillFromMatchTipsTitle")}
          className="btn-ghost text-sm sm:text-base px-4 py-2.5 sm:px-5 sm:py-3 rounded-xl border border-[var(--line)] hover:border-[var(--accent)] disabled:opacity-50"
        >
          {fillBusy ? t("project.harryBoyPreparing") : t("standings.fillFromMatchTips")}
        </button>
        <button
          type="button"
          data-testid="btn-clear-standings-placements"
          onClick={clearStandingsPlacements}
          disabled={groupLocked || fillBusy || clearBusy || ifylldaPlatser === 0}
          title={t("standings.clearPlacementsTitle")}
          className="btn-ghost text-sm sm:text-base px-4 py-2.5 sm:px-5 sm:py-3 rounded-xl border border-[var(--line)] hover:border-[var(--accent)] disabled:opacity-50"
        >
          {clearBusy ? t("project.harryBoyPreparing") : t("standings.clearPlacements")}
        </button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 max-sm:gap-0 max-sm:divide-y max-sm:divide-[var(--line)]">
        {groups.map(g => {
          const rows = tableData[g] || [];
          const codes = project.groups[g];
          const gb = groupBets[g] || {};
          return (
            <div key={g} className={`${panelCard} p-4 sm:p-5`}>
              <div className="font-display text-3xl mb-3">{t("flat.groupPrefix")} {g}</div>
              <table className="w-full text-xs font-mono mb-4 table-fixed">
                <thead className="text-[var(--text-dim)]">
                  <tr>
                    <th className="text-left w-[52%] sm:w-auto">{t("flat.teamCol")}</th>
                    <StandingsStatHeaderCells t={t} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.code} className="border-t border-[var(--line)]">
                      <td className="py-1.5 min-w-0">
                        <div className="flex min-w-0 items-center gap-2">
                        <FlagCircle code={r.code} size={20} logoUrl={logoByCode[r.code]}/>
                        <span className="min-w-0 truncate">{teamsByCode[r.code]}</span>
                        {mode === "live" && liveCodes.has(r.code) && <span className="text-danger" title={t("flat.playingNow")}>*</span>}
                        </div>
                      </td>
                      <StandingsStatBodyCells row={r} />
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="space-y-2 text-xs">
                <div className="text-[var(--text-dim)] uppercase tracking-wider">{t("standings.predictPlacement")}</div>
                {["first","second","third"].map((field, i) => {
                  const otherSelected = ["first","second","third"].filter(f=>f!==field).map(f=>gb[f]).filter(Boolean);
                  const available = codes.filter(c => !otherSelected.includes(c));
                  return (
                    <div key={field} className="flex items-center gap-2">
                      <span className="w-12 text-[var(--text-dim)]">{i===0?t("standings.place1"):i===1?t("standings.place2"):t("standings.place3")}</span>
                      <Select value={gb[field] || ""} onValueChange={v => updatePred(g, field, v)} disabled={groupLocked}>
                        <SelectTrigger data-testid={`group-${g}-${field}`} className="bg-[var(--bg-2)] border-[var(--line-2)] flex-1">
                          <SelectValue placeholder={t("standings.pickTeam")}>{gb[field] ? teamsByCode[gb[field]] : null}</SelectValue>
                        </SelectTrigger>
                        <SelectContent className="bg-[var(--panel)] border-[var(--line-2)]">
                          {available.map(c => <SelectItem key={c} value={c}>{teamsByCode[c]}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>
              <GroupStandingsMatchList
                matches={groupMatchesByLetter[g]}
                teamsByCode={teamsByCode}
                logoByCode={logoByCode}
                lang={lang}
                t={t}
              />
            </div>
          );
        })}
      </div>
      {mode === "live" && <p className="text-xs text-[var(--text-dim)] mt-3 text-right px-3 sm:px-0">{t("project.liveTeamsHint")}</p>}
    </div>
  );
}

function sortRowsBy(rows, metricFn, lang) {
  if (!rows.length) return rows;
  const loc = lang === "en" ? "en" : "sv";
  return [...rows]
    .sort((a, b) => {
      const va = metricFn(a);
      const vb = metricFn(b);
      if (vb !== va) return vb - va;
      return (a.name || "").localeCompare(b.name || "", loc);
    })
    .map((r, i) => ({ ...r, rank: i + 1 }));
}

function bd(r, key) {
  return r.breakdown?.[key] ?? 0;
}

function maxBreakdown(rows, key) {
  let mx = 0;
  for (const r of rows || []) {
    const v = bd(r, key);
    if (v > mx) mx = v;
  }
  return mx;
}

function LeaderboardBarChart({ rows, t }) {
  const segments = useMemo(
    () => [
      { id: "matches_group", label: t("lb.colGroupMatches"), color: "bg-sky-500" },
      { id: "groups", label: t("lb.colGroups"), color: "bg-emerald-500" },
      { id: "matches_ko", label: t("lb.colKoMatches"), color: "bg-indigo-500" },
      { id: "finals", label: t("lb.colFinals"), color: "bg-amber-500" },
      { id: "topscorer", label: t("lb.colTop"), color: "bg-fuchsia-500" },
    ],
    [t],
  );
  return <LeaderboardChartBlock rows={rows} segments={segments} title={t("lb.chart.title")} t={t} />;
}

function LeaderboardAccuracyBarChart({ rows, t }) {
  const segments = useMemo(
    () => [
      { id: "accuracy_1x2", label: t("lb.chart2.seg1x2"), color: "bg-teal-500" },
      { id: "accuracy_exact", label: t("lb.chart2.segExact"), color: "bg-orange-500" },
      { id: "accuracy_group_place", label: t("lb.chart2.segGroup"), color: "bg-green-600" },
      { id: "accuracy_topscorer", label: t("lb.chart2.segGoals"), color: "bg-fuchsia-500" },
      { id: "accuracy_other", label: t("lb.chart2.segOther"), color: "bg-slate-500" },
    ],
    [t],
  );
  return <LeaderboardChartBlock rows={rows} segments={segments} title={t("lb.chart2.title")} t={t} />;
}

function LeaderboardHub({ board, rules, project, picksMatrix, teamsByCode, leaderboardHubBusy = false }) {
  const { lang, t } = useLocale();
  const rows = board.rows || EMPTY_ARR;
  const scorers = board.scorers || EMPTY_ARR;
  const picksByPlayer = board.picks_by_player || {};
  const [view, setView] = useState("matrix");
  const leaderBonus = rules?.topscorer_leader_bonus ?? 0;
  const scorerTeamLogo = useMemo(() => {
    if (!project?.teams) return {};
    return Object.fromEntries(project.teams.filter((tm) => tm.logoUrl).map((tm) => [String(tm.code), tm.logoUrl]));
  }, [project]);

  const nav = [
    { id: "matrix", label: t("lb.nav.matrixOverview"), hint: t("lb.nav.matrixOverviewHint"), Icon: LayoutGrid },
    { id: "points", label: t("lb.nav.pointsBoard"), hint: t("lb.nav.pointsBoardHint"), Icon: Trophy },
    { id: "chart", label: t("lb.nav.chart"), hint: t("lb.nav.chartHint"), Icon: BarChart2 },
    { id: "topscorer", label: t("lb.nav.topscorer"), hint: t("lb.nav.topscorerHint"), Icon: Award },
  ];

  const overviewRows = sortRowsBy(rows, (r) => r.score, lang);
  const displayRows = overviewRows;
  const topscorerLbRows = sortRowsBy(rows, (r) => bd(r, "topscorer"), lang);

  const allZeroTotal = rows.length > 0 && rows.every((r) => r.score === 0);
  const categoryEmpty = false;
  const highlightCol = null;

  const memberNameById = useMemo(
    () => Object.fromEntries(rows.map((r) => [String(r.user_id), bettorDisplayName(r.name)])),
    [rows],
  );
  const topscorerPickByUser = board.topscorer_picks_by_user || {};
  const topscorerGoalsByUser = board.topscorer_goals_by_user || {};

  if (leaderboardHubBusy && rows.length === 0) {
    return (
      <div className="w-full sm:max-w-4xl sm:mx-auto px-3 py-16 text-center">
        <p className="text-[var(--text-dim)]">{t("common.loading")}</p>
      </div>
    );
  }

  return (
    <div className="w-full sm:max-w-4xl sm:mx-auto space-y-6">
      <div className="text-center px-3 sm:px-0">
        <h2 className={TAB_MAIN_TITLE_CLASS}>{t("lb.title")}</h2>
        <p className="text-sm text-[var(--text-dim)] mt-2">{t("lb.subtitle")}</p>
      </div>
      <div className="flex flex-wrap gap-2 justify-center px-3 sm:px-0">
        {nav.map(({ id, label, hint, Icon }) => (
          <button
            key={id}
            type="button"
            data-testid={`lb-nav-${id}`}
            onClick={() => setView(id)}
            title={hint}
            className={`flex items-center gap-2 px-3 py-2 rounded-full border text-xs sm:text-sm transition-colors ${
              view === id
                ? "tab-active border-[var(--accent)]"
                : "border-[var(--line-2)] text-[var(--text-dim)] hover:border-[var(--accent)]/60 hover:text-[var(--accent)]"
            }`}
          >
            <Icon size={16} className="shrink-0 opacity-90" />
            <span className="font-medium">{label}</span>
          </button>
        ))}
      </div>

      {view === "chart" ? (
        <div className={`${panelCardOverflow} p-4 sm:p-5`}>
          <LeaderboardBarChart rows={rows} t={t} />
          <div className="mt-8 pt-6 border-t border-[var(--line)]">
            <LeaderboardAccuracyBarChart rows={rows} t={t} />
          </div>
        </div>
      ) : view === "topscorer" ? (
        <div className="space-y-8">
          <div>
            <h3 className="text-center font-display text-lg mb-2 text-[var(--accent)]">{t("lb.topscorerPointsTitle")}</h3>
            <div className={panelCardOverflow}>
              <div className="max-w-full min-w-0 overflow-x-hidden sm:overflow-x-auto">
                <table className="table-auto sm:table-fixed w-full min-w-0 sm:min-w-[520px] text-[11px] sm:text-sm">
                  <colgroup className="hidden sm:table-column-group">
                    <col className="w-[6%] sm:min-w-[2.25rem]" />
                    <col className="w-[22%] sm:min-w-[6.5rem]" />
                    <col className="min-w-0 sm:min-w-[7rem]" />
                    <col className="w-[12%] sm:min-w-[3rem]" />
                    <col className="w-[14%] sm:min-w-[3.25rem]" />
                  </colgroup>
                  <thead>
                  <tr className="border-b border-[var(--line)] text-[var(--text-dim)] uppercase text-[11px] sm:text-xs">
                    <th className="px-0.5 py-1 sm:p-4 text-left whitespace-nowrap">{t("lb.colRank")}</th>
                    <th className="px-0.5 py-1 sm:p-4 text-left whitespace-nowrap">{t("lb.colPlayer")}</th>
                    <th title={t("lb.pickMatrix.extraTopscorer")} className="px-px py-1 sm:p-4 text-left whitespace-nowrap">{t("lb.pickMatrix.extraTopscorer")}</th>
                    <th title={t("lb.colTop")} className="px-px py-1 sm:p-4 text-center sm:text-right whitespace-nowrap">{t("lb.colTop")}</th>
                    <th title={t("lb.colTotal")} className="px-px py-1 sm:p-4 text-center sm:text-right whitespace-nowrap">{t("lb.colTotal")}</th>
                  </tr>
                  </thead>
                  <tbody>
                  {topscorerLbRows.map((r) => (
                    <tr key={r.user_id} className="border-b border-[var(--line)]" data-testid={`lb-row-${r.user_id}`}>
                      <td className="py-1 pl-0.5 pr-0 sm:p-4 sm:pr-4 font-display text-base sm:text-2xl tabular-nums">{r.rank}</td>
                      <td className="max-w-0 py-1 pl-0 pr-0.5 sm:p-4 sm:pl-4 truncate text-sm sm:text-base font-medium" title={r.name}>{bettorDisplayName(r.name)}</td>
                      <td className="px-px py-1 sm:p-4 text-[var(--text-dim)] truncate" title={topscorerPickByUser[String(r.user_id)] || ""}>
                        {topscorerPickByUser[String(r.user_id)] || "—"}
                      </td>
                      <td className="px-px py-1 sm:p-4 text-center sm:text-right font-mono text-[var(--accent-2)] tabular-nums">
                        {topscorerPickByUser[String(r.user_id)] ? (topscorerGoalsByUser[String(r.user_id)] ?? 0) : "—"}
                      </td>
                      <td className="px-px py-1 text-center sm:p-4 sm:text-right font-display text-lg sm:text-2xl text-[var(--accent)] tabular-nums">{bd(r, "topscorer")}</td>
                    </tr>
                  ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className={panelCardOverflow}>
            <div className="px-4 py-3 border-b border-[var(--line)] text-xs sm:text-sm text-[var(--text-dim)]">
              {Number(rules?.topscorer_award_mode || 0) === 1
                ? t("lb.scorersSubtitleAllPicks", { pts: leaderBonus })
                : t("lb.scorersSubtitle", { pts: leaderBonus })}
            </div>
            {!scorers.length ? (
              <p className="p-6 text-sm text-[var(--text-dim)] text-center">{t("lb.scorersEmpty")}</p>
            ) : (
              <div className="max-w-full min-w-0 overflow-x-hidden sm:overflow-x-auto">
                <table className="table-fixed w-full min-w-0 text-[11px] sm:text-sm">
                  <colgroup>
                    <col className="w-[6%] sm:w-[5%]" />
                    <col className="w-[38%] sm:w-[32%]" />
                    <col className="w-[10%] sm:w-[8%]" />
                    <col className="w-[10%] sm:w-[8%]" />
                    <col className="w-[36%] sm:w-[47%]" />
                  </colgroup>
                  <thead>
                    <tr className="border-b border-[var(--line)] text-[var(--text-dim)] uppercase text-[11px] sm:text-xs">
                      <th className="px-0.5 py-1 sm:p-4 text-left whitespace-nowrap">{t("lb.scorerColRank")}</th>
                      <th className="px-0.5 py-1 sm:p-4 text-left whitespace-nowrap">{t("lb.scorerColPlayer")}</th>
                      <th className="px-px py-1 sm:p-4 text-center whitespace-nowrap">{t("lb.scorerColTeam")}</th>
                      <th className="px-px py-1 sm:p-4 text-center sm:text-right whitespace-nowrap">{t("lb.scorerColGoals")}</th>
                      <th className="px-px py-1 sm:p-4 text-left whitespace-nowrap">{t("lb.scorerColTips")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scorers.slice(0, 100).map((s, idx) => {
                      const tips = picksByPlayer[String(s.id)] || [];
                      const tipsLine = tips.length
                        ? [...new Set(tips.map((x) => memberNameById[String(x.user_id)]).filter(Boolean))].join(" · ") || "—"
                        : "—";
                      return (
                        <tr key={s.id} className="border-b border-[var(--line)]">
                          <td className="py-1 pl-0.5 pr-0 sm:p-4 sm:pr-4 font-display text-base sm:text-2xl tabular-nums">{idx + 1}</td>
                          <td className="max-w-0 py-1 pl-0 pr-0.5 sm:p-4 sm:pl-4 truncate text-sm sm:text-base font-medium" title={s.name || undefined}>{s.name}</td>
                          <td className="px-px py-1 sm:p-4">
                            <div className="flex justify-center">
                              {s.team_code ? (
                                <FlagCircle code={s.team_code} size={22} logoUrl={scorerTeamLogo[String(s.team_code)]} />
                              ) : (
                                <span className="text-[var(--text-dim)]">—</span>
                              )}
                            </div>
                          </td>
                          <td className="px-px py-1 sm:p-4 text-center sm:text-right font-mono text-[var(--accent-2)] tabular-nums">{s.goals}</td>
                          <td className="px-px py-1 sm:p-4 text-[var(--text-dim)] truncate" title={tipsLine !== "—" ? tipsLine : undefined}>
                            {tipsLine}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : view === "matrix" ? (
        <div className="px-0 sm:px-0">
          <LeaderboardPicksMatrix project={project} picksMatrix={picksMatrix} leaderboardRows={rows} />
        </div>
      ) : (
        <div className={panelCardOverflow}>
          {view === "points" && (() => {
            const mxGroup = maxBreakdown(displayRows, "matches_group");
            const mxGroups = maxBreakdown(displayRows, "groups");
            const mxKo = maxBreakdown(displayRows, "matches_ko");
            const mxFinals = maxBreakdown(displayRows, "finals");
            const mxTop = maxBreakdown(displayRows, "topscorer");
            const mxTotal = Math.max(0, ...displayRows.map((r) => Number(r.score) || 0));

            const cell = (v, isMax) => (
              <span className={isMax ? "font-extrabold text-[var(--accent)]" : ""}>{v}</span>
            );

            return (
          <div className="max-w-full min-w-0 overflow-x-hidden sm:overflow-x-auto">
          <table className="table-auto sm:table-fixed w-full min-w-0 sm:min-w-[520px] text-[11px] sm:text-sm">
            <colgroup className="hidden sm:table-column-group">
              <col className="w-[6%] sm:min-w-[2.25rem]" />
              <col className="w-[20%] sm:min-w-[6.5rem]" />
              <col className="w-[12%] sm:min-w-[3.1rem]" />
              <col className="w-[12%] sm:min-w-[3.1rem]" />
              <col className="w-[12%] sm:min-w-[3.1rem]" />
              <col className="w-[9%] sm:min-w-[2.75rem]" />
              <col className="w-[9%] sm:min-w-[2.75rem]" />
              <col className="w-[20%] sm:min-w-[3.25rem]" />
            </colgroup>
            <thead>
              <tr className="border-b border-[var(--line)] text-[var(--text-dim)] uppercase text-[11px] sm:text-xs">
                <th className="px-0.5 py-1 sm:p-4 text-left whitespace-nowrap">{t("lb.colRank")}</th>
                <th className="px-0.5 py-1 sm:p-4 text-left whitespace-nowrap">{t("lb.colPlayer")}</th>
                <th title={t("lb.colGroupMatches")} className="px-px py-1 sm:p-4 text-center sm:text-right whitespace-nowrap">{t("lb.colGroupMatches")}</th>
                <th title={t("lb.colGroups")} className="px-px py-1 sm:p-4 text-center sm:text-right whitespace-nowrap">{t("lb.colGroups")}</th>
                <th title={t("lb.colKoMatches")} className="px-px py-1 sm:p-4 text-center sm:text-right whitespace-nowrap">{t("lb.colKoMatches")}</th>
                <th title={t("lb.colFinals")} className="px-px py-1 sm:p-4 text-center sm:text-right whitespace-nowrap">{t("lb.colFinals")}</th>
                <th title={t("lb.colTop")} className="px-px py-1 sm:p-4 text-center sm:text-right whitespace-nowrap">{t("lb.colTop")}</th>
                <th title={t("lb.colTotal")} className="px-px py-1 sm:p-4 text-center sm:text-right whitespace-nowrap">{t("lb.colTotal")}</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((r) => {
                const vGroup = bd(r, "matches_group");
                const vGroups = bd(r, "groups");
                const vKo = bd(r, "matches_ko");
                const vFinals = bd(r, "finals");
                const vTop = bd(r, "topscorer");
                return (
                  <tr key={r.user_id} className="border-b border-[var(--line)]" data-testid={`lb-row-${r.user_id}`}>
                    <td className="py-1 pl-0.5 pr-0 sm:p-4 sm:pr-4 font-display text-base sm:text-2xl tabular-nums">{r.rank}</td>
                    <td className="max-w-0 py-1 pl-0 pr-0.5 sm:p-4 sm:pl-4 truncate text-sm sm:text-base font-medium" title={r.name}>{bettorDisplayName(r.name)}</td>
                    <td className="px-px py-1 sm:p-4 text-center sm:text-right font-mono tabular-nums">{cell(vGroup, mxGroup > 0 && vGroup === mxGroup)}</td>
                    <td className="px-px py-1 sm:p-4 text-center sm:text-right font-mono tabular-nums">{cell(vGroups, mxGroups > 0 && vGroups === mxGroups)}</td>
                    <td className="px-px py-1 sm:p-4 text-center sm:text-right font-mono tabular-nums">{cell(vKo, mxKo > 0 && vKo === mxKo)}</td>
                    <td className="px-px py-1 sm:p-4 text-center sm:text-right font-mono tabular-nums">{cell(vFinals, mxFinals > 0 && vFinals === mxFinals)}</td>
                    <td className="px-0 py-1 sm:p-4 text-center sm:text-right font-mono tabular-nums">{cell(vTop, mxTop > 0 && vTop === mxTop)}</td>
                    <td className="px-px py-1 text-center sm:p-4 sm:text-right font-display text-lg sm:text-2xl text-[var(--accent)] tabular-nums">{cell(r.score, mxTotal > 0 && Number(r.score) === mxTotal)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

function inferTopscorerPickFromOlRow(row) {
  if (!row) return { last: "", team: "" };
  const parts = String(row.name || "")
    .trim()
    .split(/[\s,]+/)
    .filter(Boolean);
  const last = parts.length ? parts[parts.length - 1] : "";
  return { last, team: row.team_code != null ? String(row.team_code) : "" };
}

function ExtrasView({ project, finalsBet, setFinalsBet, reload }) {
  const { lang, t } = useLocale();
  const [f1, setF1] = useState(finalsBet?.finalists?.[0] || "");
  const [f2, setF2] = useState(finalsBet?.finalists?.[1] || "");
  const [w, setW] = useState(finalsBet?.winner || "");
  const [tsLast, setTsLast] = useState((finalsBet?.topscorer_lastname || "").trim());
  const [tsTeam, setTsTeam] = useState((finalsBet?.topscorer_team_code || "").trim());
  const [finalsDirty, setFinalsDirty] = useState(false);
  const olScorers = useMemo(() => project.ol_scorers || [], [project.ol_scorers]);

  useEffect(() => {
    setFinalsDirty(false);
  }, [project.id]);

  useEffect(() => {
    if (finalsDirty) return;
    setF1(finalsBet?.finalists?.[0] || "");
    setF2(finalsBet?.finalists?.[1] || "");
    setW(finalsBet?.winner || "");
    const ln = (finalsBet?.topscorer_lastname || "").trim();
    const tc = (finalsBet?.topscorer_team_code || "").trim();
    if (ln || tc) {
      setTsLast(ln);
      setTsTeam(tc);
    } else if (finalsBet?.topscorer_player_id != null && olScorers.length) {
      const row = olScorers.find((s) => String(s.id) === String(finalsBet.topscorer_player_id));
      const inf = inferTopscorerPickFromOlRow(row);
      setTsLast(inf.last);
      setTsTeam(inf.team);
    } else {
      setTsLast("");
      setTsTeam("");
    }
  }, [
    finalsBet?.finalists,
    finalsBet?.winner,
    finalsBet?.topscorer_lastname,
    finalsBet?.topscorer_team_code,
    finalsBet?.topscorer_player_id,
    olScorers,
    finalsDirty,
  ]);

  const markFinalsDirty = () => setFinalsDirty(true);
  const groupLocked = isGroupPhaseLocked(project.fixtures || []);
  const save = async () => {
    if (groupLocked) {
      toast.error(t("project.harryBoyGroupLocked"));
      return;
    }
    const last = tsLast.trim();
    const team = tsTeam.trim();
    try {
      await axios.post(`${API}/projects/${project.id}/bets/finals`, {
        finalists: [f1, f2].filter(Boolean),
        winner: w,
        topscorer: null,
        topscorer_player_id: null,
        topscorer_lastname: last || null,
        topscorer_team_code: team || null,
      });
      toast.success(t("common.saved"));
      setFinalsDirty(false);
      if (reload) await reload();
    } catch (e) {
      toast.error(formatApiErrorMessage(e, t("project.saveFailed")) || t("project.saveFailed"));
    }
  };
  const teams = project.teams;
  const teamsByCode = Object.fromEntries(
    teams.map((tm) => [tm.code, teamDisplayName(tm.code, tm.name, lang)]),
  );
  const tkey = project.tournament_key || "WC2026";
  const teamPickOptions = useMemo(
    () => buildTopscorerTeamOptions(teams, tkey, lang),
    [teams, tkey, lang],
  );
  const headlineTeamOpts = useMemo(() => teamPickOptions.filter((o) => o.section === "top"), [teamPickOptions]);
  const restTeamOpts = useMemo(() => teamPickOptions.filter((o) => o.section === "rest"), [teamPickOptions]);
  // Available pools
  const f1Pool = teams.filter(t => t.code !== f2);
  const f2Pool = teams.filter(t => t.code !== f1);
  return (
    <div className="w-full sm:max-w-2xl sm:mx-auto space-y-8">
      <div className="text-center px-3 sm:px-0">
        <h2 className={TAB_MAIN_TITLE_CLASS}>{t("extras.title")}</h2>
      </div>
      <div className={`${panelCard} px-3 py-6 sm:p-8 space-y-6`}>
      {groupLocked && (
        <p className="text-sm text-[var(--text-dim)]">{t("project.groupPhaseLockedHint")}</p>
      )}
      <div className="grid grid-cols-2 gap-4">
        <div><Label>{t("extras.finalist1")}</Label>
          <Select value={f1} onValueChange={(v)=>{markFinalsDirty(); setF1(v);}} disabled={groupLocked}>
            <SelectTrigger data-testid="final-1" className="bg-[var(--bg-2)] border-[var(--line-2)]">
              <SelectValue placeholder={t("extras.teamPlaceholder")}>{f1 ? teamsByCode[f1] : null}</SelectValue>
            </SelectTrigger>
            <SelectContent className="bg-[var(--panel)] border-[var(--line-2)] max-h-72">
              {f1Pool.map((tm) => <SelectItem key={tm.code} value={tm.code}>{teamDisplayName(tm.code, tm.name, lang)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label>{t("extras.finalist2")}</Label>
          <Select value={f2} onValueChange={(v)=>{markFinalsDirty(); setF2(v);}} disabled={groupLocked}>
            <SelectTrigger data-testid="final-2" className="bg-[var(--bg-2)] border-[var(--line-2)]">
              <SelectValue placeholder={t("extras.teamPlaceholder")}>{f2 ? teamsByCode[f2] : null}</SelectValue>
            </SelectTrigger>
            <SelectContent className="bg-[var(--panel)] border-[var(--line-2)] max-h-72">
              {f2Pool.map((tm) => <SelectItem key={tm.code} value={tm.code}>{teamDisplayName(tm.code, tm.name, lang)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div><Label>{t("extras.winner")}</Label>
        <Select value={w} onValueChange={(v) => { markFinalsDirty(); setW(v); }} disabled={groupLocked}>
          <SelectTrigger data-testid="winner-sel" className="bg-[var(--bg-2)] border-[var(--line-2)]">
            <SelectValue placeholder={t("extras.teamPlaceholder")}>{w ? teamsByCode[w] : null}</SelectValue>
          </SelectTrigger>
          <SelectContent className="bg-[var(--panel)] border-[var(--line-2)] max-h-72">
            {teams.map((tm) => <SelectItem key={tm.code} value={tm.code}>{teamDisplayName(tm.code, tm.name, lang)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-3">
        <Label>{t("extras.topscorer")}</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-[var(--text-dim)]">{t("extras.topscorerLastname")}</Label>
            <Input
              data-testid="topscorer-input"
              value={tsLast}
              onChange={(e) => { markFinalsDirty(); setTsLast(e.target.value); }}
              placeholder={t("extras.topscorerLastnamePh")}
              className="mt-1 bg-[var(--bg-2)] border-[var(--line-2)]"
              autoComplete="off"
              disabled={groupLocked}
            />
          </div>
          <div>
            <Label className="text-xs text-[var(--text-dim)]">
              {t("extras.topscorerTeamLabelWC")}
            </Label>
            <Select
              value={tsTeam || "__ts_team_none__"}
              onValueChange={(v) => { markFinalsDirty(); setTsTeam(v === "__ts_team_none__" ? "" : v); }}
              disabled={groupLocked}
            >
              <SelectTrigger data-testid="topscorer-team" className="mt-1 bg-[var(--bg-2)] border-[var(--line-2)]">
                <SelectValue placeholder={t("extras.topscorerTeamPlaceholder")} />
              </SelectTrigger>
              <SelectContent className="bg-[var(--panel)] border-[var(--line-2)] max-h-72">
                <SelectItem value="__ts_team_none__">{t("extras.topscorerClear")}</SelectItem>
                {headlineTeamOpts.length > 0 && (
                  <SelectGroup>
                    <SelectLabel className="text-[var(--text-dim)]">{t("extras.topscorerTeamHeadline")}</SelectLabel>
                    {headlineTeamOpts.map((o) => (
                      <SelectItem key={o.code} value={o.code}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )}
                {headlineTeamOpts.length > 0 && restTeamOpts.length > 0 ? <SelectSeparator /> : null}
                {restTeamOpts.length > 0 && (
                  <SelectGroup>
                    {headlineTeamOpts.length > 0 ? (
                      <SelectLabel className="text-[var(--text-dim)]">{t("extras.topscorerTeamRest")}</SelectLabel>
                    ) : null}
                    {restTeamOpts.map((o) => (
                      <SelectItem key={o.code} value={o.code}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>
        <p className="text-xs text-[var(--text-dim)]">{t("extras.topscorerAutoHint")}</p>
        <p className="text-xs text-[var(--text-dim)]">
          {project.rules?.topscorer_leader_bonus ?? 0}{" "}
          {Number(project.rules?.topscorer_award_mode ?? 0) === 1 ? t("extras.topscorerPtsAllScorers") : t("extras.topscorerPts")}
        </p>
      </div>
      <button data-testid="btn-save-finals" onClick={save} disabled={groupLocked} className="btn-primary disabled:opacity-50">{t("common.save")}</button>
      </div>
    </div>
  );
}

function sortPrizeSlotsByPlace(arr) {
  return [...arr].sort((a, b) => (Number(a.place) || 0) - (Number(b.place) || 0));
}

/** Radordning = plats 1…n; uppdatera place efter varje ändring. */
function renumberPrizeSlotsInOrder(arr) {
  return arr.map((row, i) => ({ ...row, place: i + 1 }));
}

function normalizePrizeSlotsFromProject(project) {
  const raw = project.prize_slots || [];
  const mapped = raw.map((s) => ({
    place: s.place,
    mode: s.mode,
    value: s.mode === "stake" ? "" : s.value ?? 0,
  }));
  if (mapped.length === 0) {
    return renumberPrizeSlotsInOrder([
      { place: 1, mode: "percent", value: 0 },
      { place: 2, mode: "percent", value: 0 },
    ]);
  }
  return renumberPrizeSlotsInOrder(sortPrizeSlotsByPlace(mapped));
}

function prizeModeShortLabel(mode, t) {
  if (mode === "percent") return t("settings.economyModePercentShort");
  if (mode === "currency") return t("settings.economyModeCurrencyShort");
  return t("settings.economyModeStakeShort");
}

function EconomySettingsSection({ project, isStaff, reload }) {
  const { t, lang } = useLocale();
  const membersCount = project.members?.length ?? 0;
  const [stake, setStake] = useState(String(project.stake ?? 0));
  const [currency, setCurrency] = useState(project.currency_code || "SEK");
  const [slots, setSlots] = useState(() => normalizePrizeSlotsFromProject(project));
  const [swishPhoneInput, setSwishPhoneInput] = useState(() => swishPhoneToInputValue(project.swish_phone));
  const [economyDirty, setEconomyDirty] = useState(false);

  useEffect(() => {
    setEconomyDirty(false);
  }, [project.id]);

  useEffect(() => {
    if (economyDirty) return;
    setStake(String(project.stake ?? 0));
    setCurrency(project.currency_code || "SEK");
    setSlots(normalizePrizeSlotsFromProject(project));
    setSwishPhoneInput(swishPhoneToInputValue(project.swish_phone));
  }, [project, economyDirty]);

  const markEconomyDirty = () => setEconomyDirty(true);

  const breakdown = useMemo(() => {
    const apiSlots = renumberPrizeSlotsInOrder(slots).map(normalizeSlotForApi);
    return computePrizeBreakdown(Number(stake) || 0, membersCount, apiSlots);
  }, [stake, membersCount, slots]);

  const swishPhoneValidation = useMemo(() => validateSwishPhone(swishPhoneInput), [swishPhoneInput]);

  const swishPayParams = useMemo(() => {
    if ((currency || "").toUpperCase() !== "SEK") return null;
    const phoneForLink = isStaff
      ? swishPhoneValidation.phone || project.swish_phone
      : project.swish_phone;
    if (!phoneForLink) return null;
    return {
      phone: phoneForLink,
      amountSek: Number(stake) || 0,
      message: buildSwishMessage(project.name),
      currencyCode: currency,
    };
  }, [currency, isStaff, swishPhoneValidation.phone, project.swish_phone, project.name, stake]);

  const swishPaymentUrl = useMemo(
    () => (swishPayParams ? buildSwishPaymentUrl(swishPayParams) : null),
    [swishPayParams],
  );

  const swishQrContent = useMemo(
    () => (swishPayParams ? buildSwishQrContent(swishPayParams) : null),
    [swishPayParams],
  );

  const updateSlot = (i, patch) => {
    markEconomyDirty();
    const { place: _ignorePlace, ...rest } = patch;
    setSlots((prev) =>
      renumberPrizeSlotsInOrder(prev.map((row, j) => (j === i ? { ...row, ...rest } : row))),
    );
  };

  const addRow = () => {
    markEconomyDirty();
    setSlots((prev) =>
      renumberPrizeSlotsInOrder([...prev, { place: prev.length + 1, mode: "percent", value: 0 }]),
    );
  };

  const removeSlot = (i) => {
    markEconomyDirty();
    setSlots((prev) => renumberPrizeSlotsInOrder(prev.filter((_, j) => j !== i)));
  };

  const saveEconomy = async () => {
    if (isStaff && swishPhoneInput.trim() && swishPhoneValidation.error) {
      toast.error(t("settings.swishInvalid"));
      return;
    }
    try {
      const body = {
        stake: Number(stake) || 0,
        currency_code: currency,
        prize_slots: renumberPrizeSlotsInOrder(slots).map(normalizeSlotForApi),
      };
      if (isStaff) {
        body.swish_phone = swishPhoneInput.trim() ? swishPhoneInput : "";
      }
      await axios.patch(`${API}/projects/${project.id}/economy`, body);
      toast.success(t("settings.economySaved"));
      setEconomyDirty(false);
      await reload();
    } catch (e) {
      toast.error(formatApiErrorMessage(e, t("settings.memberErr")) || t("settings.memberErr"));
    }
  };

  return (
    <section className={SETTINGS_SECTION_DIVIDER}>
      <h3 className={SETTINGS_SECTION_TITLE}>{t("settings.sectionEconomy")}</h3>
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <div className={SETTINGS_META}>{t("settings.economyPot")}</div>
            <div className="font-display text-2xl text-[var(--accent)] tabular-nums">
              {formatCurrencyAmount(breakdown.pot, currency, lang)}
            </div>
            <div className={`${SETTINGS_META} mt-1`}>{t("settings.economyFormula")}</div>
          </div>
          <div className={SETTINGS_HINT}>
            {membersCount} {t("dashboard.participants")}
          </div>
        </div>

        {isStaff ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">{t("settings.economyStakeLabel")}</Label>
              <Input
                type="number"
                min={0}
                step="any"
                value={stake}
                onChange={(e) => {
                  markEconomyDirty();
                  setStake(e.target.value);
                }}
                className="mt-1 bg-[var(--bg-2)] border-[var(--line-2)]"
              />
            </div>
            <div>
              <Label className="text-xs">{t("settings.economyCurrencyLabel")}</Label>
              <Select value={currency} onValueChange={(v) => { markEconomyDirty(); setCurrency(v); }}>
                <SelectTrigger className="mt-1 bg-[var(--bg-2)] border-[var(--line-2)]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[var(--panel)] border-[var(--line-2)] max-h-72">
                  {CURRENCY_CODES_ORDERED.map((code) => (
                    <SelectItem key={code} value={code}>
                      {currencySelectLabel(code, lang)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : (
          <p className={SETTINGS_HINT}>{t("settings.economyReadOnly")}</p>
        )}

        {(currency || "").toUpperCase() === "SEK" && (
          <div className="rounded-lg border border-[var(--line-2)] p-4 space-y-3">
            <div>
              <div className={SETTINGS_SECTION_TITLE + " mb-0"}>{t("settings.swishTitle")}</div>
              <p className={`${SETTINGS_META} mt-1`}>{t("settings.swishHint")}</p>
            </div>
            {isStaff && (
              <div>
                <Label className="text-xs">{t("settings.swishPhoneLabel")}</Label>
                <Input
                  type="tel"
                  autoComplete="tel"
                  placeholder={t("settings.swishPhonePlaceholder")}
                  value={swishPhoneInput}
                  onChange={(e) => {
                    markEconomyDirty();
                    setSwishPhoneInput(e.target.value);
                  }}
                  className={`mt-1 bg-[var(--bg-2)] border-[var(--line-2)] ${
                    swishPhoneInput.trim() && swishPhoneValidation.error ? "border-[var(--destructive-border)]" : ""
                  }`}
                />
                {swishPhoneInput.trim() && swishPhoneValidation.error ? (
                  <p className={`${SETTINGS_META} text-[var(--destructive-text)] mt-1`}>{t("settings.swishInvalid")}</p>
                ) : null}
              </div>
            )}
            {swishQrContent && swishPaymentUrl ? (
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex flex-col items-center gap-1 shrink-0">
                  <QRCodeSVG value={swishQrContent} size={160} level="M" className="rounded-md bg-white p-2" />
                  <span className={SETTINGS_META}>{t("settings.swishScanHint")}</span>
                </div>
                <a href={swishPaymentUrl} className="btn-primary inline-flex justify-center text-center sm:self-center">
                  {t("settings.swishOpen")}
                </a>
              </div>
            ) : !isStaff && !project.swish_phone ? (
              <p className={SETTINGS_HINT}>{t("settings.economyReadOnly")}</p>
            ) : null}
          </div>
        )}

        <div>
          <div className={`${SETTINGS_SECTION_TITLE} mb-2`}>{t("settings.economyPrizeTitle")}</div>
          <div className="rounded-lg border border-[var(--line-2)] overflow-hidden">
            <table className="w-full text-xs sm:text-sm table-fixed">
              <colgroup>
                <col style={{ width: "8%" }} />
                <col style={{ width: "24%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: isStaff ? "34%" : "46%" }} />
                {isStaff ? <col style={{ width: "12%" }} /> : null}
              </colgroup>
              <thead>
                <tr className="border-b border-[var(--line-2)] text-[var(--text-dim)] text-xs uppercase">
                  <th className="px-1.5 py-1.5 sm:px-2 sm:py-2 text-left whitespace-nowrap">{t("settings.economyPlace")}</th>
                  <th className="px-1 py-1.5 sm:px-2 sm:py-2 text-left whitespace-nowrap">{t("settings.economyMode")}</th>
                  <th className="px-1 py-1.5 sm:px-2 sm:py-2 text-left whitespace-nowrap">{t("settings.economyValue")}</th>
                  <th className="px-1.5 py-1.5 sm:px-2 sm:py-2 text-right whitespace-nowrap">{t("settings.economyComputed")}</th>
                  {isStaff && <th className="px-1 py-1.5 sm:px-2 sm:py-2 text-right whitespace-nowrap sr-only">{t("settings.economyRemove")}</th>}
                </tr>
              </thead>
              <tbody>
                {breakdown.rows.map((row, i) => (
                  <tr key={`prize-slot-${i}-${row.mode}`} className="border-b border-[var(--line-2)]">
                    <td className="px-1.5 py-1.5 sm:px-2 sm:py-2 align-middle">
                      <span className="font-mono tabular-nums">{i + 1}</span>
                    </td>
                    <td className="px-1 py-1.5 sm:px-2 sm:py-2 align-middle min-w-0">
                      {isStaff ? (
                        <Select
                          value={slots[i]?.mode ?? row.mode}
                          onValueChange={(nv) =>
                            updateSlot(i, {
                              mode: nv,
                              value: nv === "stake" ? "" : Number(slots[i]?.value) || 0,
                            })
                          }
                        >
                          <SelectTrigger className="h-8 w-full min-w-0 px-2 text-xs bg-[var(--bg-2)] border-[var(--line-2)]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[var(--panel)] border-[var(--line-2)] min-w-0">
                            <SelectItem value="percent" showIndicator={false}>{t("settings.economyModePercentShort")}</SelectItem>
                            <SelectItem value="currency" showIndicator={false}>{t("settings.economyModeCurrencyShort")}</SelectItem>
                            <SelectItem value="stake" showIndicator={false}>{t("settings.economyModeStakeShort")}</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span>{prizeModeShortLabel(row.mode, t)}</span>
                      )}
                    </td>
                    <td className="px-1 py-1.5 sm:px-2 sm:py-2 align-middle min-w-0">
                      {isStaff && (slots[i]?.mode ?? row.mode) !== "stake" ? (
                        <Input
                          type="number"
                          min={0}
                          step="any"
                          value={slots[i]?.value ?? ""}
                          onChange={(e) => updateSlot(i, { value: e.target.value })}
                          className="h-8 w-full min-w-0 px-2 text-xs bg-[var(--bg-2)] border-[var(--line-2)]"
                        />
                      ) : (
                        <span className="text-[var(--text-dim)] whitespace-nowrap">
                          {row.mode === "stake"
                            ? "—"
                            : row.mode === "percent"
                              ? `${row.value ?? 0}%`
                              : formatCurrencyAmount(Number(row.value ?? 0), currency, lang)}
                        </span>
                      )}
                    </td>
                    <td className="px-1.5 py-1.5 sm:px-2 sm:py-2 text-right font-mono tabular-nums text-[var(--accent-2)] align-middle whitespace-nowrap">
                      {formatCurrencyAmount(row.computed, currency, lang)}
                    </td>
                    {isStaff && (
                      <td className="px-0.5 py-1.5 sm:px-1 sm:py-2 align-middle text-right">
                        <button
                          type="button"
                          onClick={() => removeSlot(i)}
                          className="inline-flex p-1 text-danger rounded-md hover:bg-danger/10 transition-colors focus:outline-none"
                          title={t("settings.economyRemove")}
                          aria-label={t("settings.economyRemove")}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <div className="flex justify-between gap-2 border border-[var(--line-2)] rounded-lg px-3 py-2">
              <span className="text-[var(--text-dim)]">{t("settings.economyAllocated")}</span>
              <span className="font-mono tabular-nums">{formatCurrencyAmount(breakdown.allocated, currency, lang)}</span>
            </div>
            <div
              className={`flex justify-between gap-2 border rounded-lg px-3 py-2 ${
                breakdown.remainder < 0
                  ? "border-[var(--destructive-border)] text-[var(--destructive-text)]"
                  : "border-[var(--line-2)]"
              }`}
            >
              <span className="text-[var(--text-dim)]">
                {breakdown.remainder < 0 ? t("settings.economyOver") : t("settings.economyRemainder")}
              </span>
              <span className="font-mono tabular-nums">
                {formatCurrencyAmount(Math.abs(breakdown.remainder), currency, lang)}
              </span>
            </div>
          </div>
          {isStaff && (
            <>
              <div className="mt-4 flex flex-wrap gap-2">
                <button type="button" onClick={addRow} className="btn-ghost flex items-center gap-2 text-sm">
                  <Plus size={16} /> {t("settings.economyAddRow")}
                </button>
              </div>
              <button type="button" onClick={saveEconomy} className="btn-primary mt-6">
                {t("settings.economySave")}
              </button>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function SettingsView({ project, user, isOwner, isStaff, reload }) {
  const navigate = useNavigate();
  const { t } = useLocale();
  const [rules, setRules] = useState(project.rules);
  const [rulesDirty, setRulesDirty] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteNameInput, setDeleteNameInput] = useState("");
  const [deleting, setDeleting] = useState(false);

  const adminSet = useMemo(() => new Set(project.admin_ids || []), [project.admin_ids]);
  const organizerIds = useMemo(() => {
    const ids = project.owner_ids;
    if (Array.isArray(ids) && ids.length) return ids;
    return project.owner_id ? [project.owner_id] : [];
  }, [project.owner_ids, project.owner_id]);
  const organizerSet = useMemo(() => new Set(organizerIds), [organizerIds]);
  const payment = project.payment_checked || {};

  const sortedMembers = useMemo(() => {
    const list = [...(project.member_users || [])];
    list.sort((a, b) => {
      if (a.id === project.owner_id) return -1;
      if (b.id === project.owner_id) return 1;
      const ao = organizerSet.has(a.id) ? 0 : 1;
      const bo = organizerSet.has(b.id) ? 0 : 1;
      if (ao !== bo) return ao - bo;
      const aa = adminSet.has(a.id) ? 0 : 1;
      const ab = adminSet.has(b.id) ? 0 : 1;
      if (aa !== ab) return aa - ab;
      return (a.name || "").localeCompare(b.name || "", "sv");
    });
    return list;
  }, [project.member_users, project.owner_id, organizerSet, adminSet]);

  useEffect(() => {
    setRulesDirty(false);
  }, [project.id]);

  useEffect(() => {
    if (rulesDirty) return;
    setRules(project.rules);
  }, [project, rulesDirty]);

  const markRulesDirty = () => setRulesDirty(true);

  const isLastOrganizer = (memberId) => organizerSet.has(memberId) && organizerIds.length <= 1;

  const canRemoveMember = (m) => {
    if (isLastOrganizer(m.id)) return false;
    if (m.id === user?.id) return true;
    if (!isStaff) return false;
    if (organizerSet.has(m.id)) return isOwner;
    if (isOwner) return true;
    return !adminSet.has(m.id);
  };

  const patchOrganizer = async (memberId, on) => {
    try {
      await axios.patch(`${API}/projects/${project.id}/member-organizer`, { user_id: memberId, organizer: on });
      toast.success(t("common.saved"));
      await reload();
    } catch (e) {
      toast.error(formatApiErrorMessage(e, t("settings.memberErr")) || t("settings.memberErr"));
    }
  };

  const patchAdmin = async (memberId, on) => {
    try {
      await axios.patch(`${API}/projects/${project.id}/member-admin`, { user_id: memberId, admin: on });
      toast.success(t("common.saved"));
      await reload();
    } catch (e) {
      toast.error(formatApiErrorMessage(e, t("settings.memberErr")) || t("settings.memberErr"));
    }
  };

  const setPaid = async (memberId, paid) => {
    try {
      await axios.patch(`${API}/projects/${project.id}/payment-checked`, { user_id: memberId, paid });
      await reload();
    } catch (e) {
      toast.error(formatApiErrorMessage(e, t("settings.memberErr")) || t("settings.memberErr"));
    }
  };

  const removeMember = async (m) => {
    const self = m.id === user?.id;
    if (!window.confirm(self ? t("settings.confirmLeave") : t("settings.confirmRemove", { name: m.name }))) return;
    try {
      await axios.delete(`${API}/projects/${project.id}/members/${m.id}`);
      toast.success(self ? t("settings.leftOk") : t("settings.removedOk"));
      await reload();
      if (self) navigate(PROJECTS_LIST_PATH);
    } catch (e) {
      toast.error(formatApiErrorMessage(e, t("settings.memberErr")) || t("settings.memberErr"));
    }
  };

  const ruleLabel = (k) => t(`settings.rule.${k}`) || k;
  const saveRules = async () => {
    await axios.patch(`${API}/projects/${project.id}/rules`, rules);
    toast.success(t("settings.rulesSaved"));
    setRulesDirty(false);
    await reload();
  };

  const maxPoints = useMemo(() => {
    const fx = project.fixtures || [];
    const groupMatches = fx.filter((m) => m?.stage === "GROUP").length;
    const koMatches = fx.filter((m) => m?.stage && m.stage !== "GROUP").length;
    const groupsCount = Object.keys(project.groups || {}).length;
    const hasThirdInGroups = groupsCount > 0; // WC2026 uses third-place picks; keep formula stable.

    const g1x2 = Number(rules?.group_1x2 ?? 0) || 0;
    const gExact = Number(rules?.group_exact ?? 0) || 0;
    const k1x2 = Number(rules?.ko_1x2 ?? 0) || 0;
    const kExact = Number(rules?.ko_exact ?? 0) || 0;

    const gFirst = Number(rules?.group_correct_first ?? 0) || 0;
    const gSecond = Number(rules?.group_correct_second ?? 0) || 0;
    const gThird = Number(rules?.group_correct_third ?? 0) || 0;

    const finalist = Number(rules?.finalist ?? 0) || 0;
    const winner = Number(rules?.winner ?? 0) || 0;

    const topBonus = Number(rules?.topscorer_leader_bonus ?? 0) || 0;
    const olScorers = project.ol_scorers || [];
    const mxGoals = olScorers.length ? Math.max(...olScorers.map((s) => Math.max(0, Number(s?.goals ?? 0) || 0))) : null;

    const matchesGroup = groupMatches * Math.max(g1x2, gExact);
    const matchesKo = koMatches * Math.max(k1x2, kExact);
    const groups = groupsCount * (gFirst + gSecond + (hasThirdInGroups ? gThird : 0));
    const finals = 2 * finalist + winner;
    const topscorer = mxGoals == null ? null : topBonus * mxGoals;

    const total = matchesGroup + matchesKo + groups + finals + (topscorer ?? 0);
    return { matchesGroup, matchesKo, groups, finals, topscorer, total, mxGoals };
  }, [project.fixtures, project.groups, project.ol_scorers, rules]);

  const inviteLink = useMemo(() => buildInviteLink(project.invite_code) || "", [project.invite_code]);

  const nameMatches = deleteNameInput.trim() === project.name.trim();
  const confirmDelete = async () => {
    if (!nameMatches || deleting) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/projects/${project.id}`);
      toast.success(t("settings.deleteOk"));
      setDeleteOpen(false);
      navigate(PROJECTS_LIST_PATH);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || t("settings.deleteErr");
      toast.error(typeof msg === "string" ? msg : t("settings.deleteErr"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="w-full sm:max-w-2xl sm:mx-auto space-y-8">
      <div className="text-center px-3 sm:px-0">
        <h2 className={TAB_MAIN_TITLE_CLASS}>{t("settings.pageTitle")}</h2>
      </div>
      <div className={`${panelCard} px-3 py-6 sm:p-8 space-y-10`}>
        <section>
          <h3 className={SETTINGS_SECTION_TITLE}>{t("settings.sectionMembers")}</h3>
          {isStaff && (
            <p className={`${SETTINGS_HINT} mb-4`}>{t("settings.paymentHint")}</p>
          )}
          {isOwner && (
            <p className={`${SETTINGS_HINT} mb-4`}>{t("settings.organizerLastHint")}</p>
          )}
          <ul className="space-y-3">
            {sortedMembers.map((m) => {
              const isRowCreator = m.id === project.owner_id;
              const isRowOrganizer = organizerSet.has(m.id);
              const isRowAdmin = adminSet.has(m.id);
              const organizerSwitchLocked = isRowOrganizer && isLastOrganizer(m.id);
              return (
                <li
                  key={m.id}
                  className="flex flex-col gap-3 border border-[var(--line-2)] rounded-xl px-4 py-3"
                  data-testid={`member-row-${m.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
                        <span className="font-medium truncate">{m.name}</span>
                        {isRowCreator && (
                          <span className={MEMBER_ROLE_BADGE}>{t("settings.badgeCreator")}</span>
                        )}
                        {isRowOrganizer && !isRowCreator && (
                          <span className={MEMBER_ROLE_BADGE}>{t("settings.badgeOrganizer")}</span>
                        )}
                        {!isRowOrganizer && isRowAdmin && (
                          <span className={MEMBER_ROLE_BADGE}>{t("settings.badgeAdmin")}</span>
                        )}
                      </div>
                      <div className="mt-0.5 text-xs text-[var(--text-dim)] break-words">{m.email}</div>
                    </div>
                    {canRemoveMember(m) && (
                      <button
                        type="button"
                        data-testid={`btn-remove-member-${m.id}`}
                        onClick={() => removeMember(m)}
                        className="btn-ghost btn-destructive shrink-0 p-2 rounded-lg self-start"
                        title={m.id === user?.id ? t("settings.leaveProject") : t("settings.removeMember")}
                      >
                        <UserMinus size={18} />
                      </button>
                    )}
                  </div>
                  {(isOwner || isStaff) && (
                    <div className="flex flex-wrap items-center gap-3 sm:gap-4 w-full justify-end">
                      {isOwner && (
                        <div className="flex items-center gap-2" data-testid={`organizer-switch-wrap-${m.id}`}>
                          <span className="text-xs text-[var(--text-dim)] whitespace-nowrap">{t("settings.organizerSwitch")}</span>
                          <Switch
                            checked={isRowOrganizer}
                            disabled={organizerSwitchLocked}
                            onCheckedChange={(on) => patchOrganizer(m.id, on)}
                            data-testid={`organizer-switch-${m.id}`}
                          />
                        </div>
                      )}
                      {isOwner && !isRowOrganizer && (
                        <div className="flex items-center gap-2" data-testid={`admin-switch-wrap-${m.id}`}>
                          <span className="text-xs text-[var(--text-dim)] whitespace-nowrap">{t("settings.adminSwitch")}</span>
                          <Switch
                            checked={isRowAdmin}
                            onCheckedChange={(on) => patchAdmin(m.id, on)}
                            data-testid={`admin-switch-${m.id}`}
                          />
                        </div>
                      )}
                      {isStaff && (
                        <div className="flex items-center gap-2" data-testid={`payment-switch-wrap-${m.id}`}>
                          <span className="text-xs text-[var(--text-dim)] whitespace-nowrap">{t("settings.paidLabel")}</span>
                          <Switch
                            checked={!!payment[m.id]}
                            onCheckedChange={(on) => setPaid(m.id, on)}
                            data-testid={`payment-switch-${m.id}`}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>

        <EconomySettingsSection project={project} isStaff={isStaff} reload={reload} />

        <section className={SETTINGS_SECTION_DIVIDER}>
          <h3 className={SETTINGS_SECTION_TITLE}>{t("settings.sectionRules")}</h3>
          <p className={`${SETTINGS_HINT} mb-4`}>{isOwner ? t("settings.rulesHintOwner") : t("settings.rulesHintMember")}</p>
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(rules)
              .filter(([k]) => k !== "topscorer_per_goal")
              .map(([k, v]) => (
              <div key={k}>
                <Label className="text-xs">{ruleLabel(k)}</Label>
                {k === "topscorer_award_mode" ? (
                  <Select
                    value={String(v ?? 0)}
                    onValueChange={(nv) => { markRulesDirty(); setRules({ ...rules, [k]: Number(nv) }); }}
                    disabled={!isOwner}
                  >
                    <SelectTrigger data-testid={`rule-${k}`} className="bg-[var(--bg-2)] border-[var(--line-2)]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--panel)] border-[var(--line-2)]">
                      <SelectItem value="0">{t("settings.topscorerAwardMode.leaderOnly")}</SelectItem>
                      <SelectItem value="1">{t("settings.topscorerAwardMode.allPicks")}</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    data-testid={`rule-${k}`}
                    type="number"
                    disabled={!isOwner}
                    value={v}
                    onChange={(e) => { markRulesDirty(); setRules({ ...rules, [k]: Number(e.target.value) }); }}
                    className="bg-[var(--bg-2)] border-[var(--line-2)]"
                  />
                )}
              </div>
            ))}
          </div>
          <div className="mt-6 border border-[var(--line-2)] rounded-xl bg-[var(--panel-2)] p-4">
            <div className={`${SETTINGS_SECTION_TITLE} mb-3`}>
              {t("settings.maxPointsTitle")}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div className="text-[var(--text-dim)]">{t("settings.maxPointsMatchesGroup")}</div>
              <div className="text-right font-mono tabular-nums text-[var(--accent-2)]">{maxPoints.matchesGroup}</div>
              <div className="text-[var(--text-dim)]">{t("settings.maxPointsMatchesKo")}</div>
              <div className="text-right font-mono tabular-nums text-[var(--accent-2)]">{maxPoints.matchesKo}</div>
              <div className="text-[var(--text-dim)]">{t("settings.maxPointsGroups")}</div>
              <div className="text-right font-mono tabular-nums text-[var(--accent-2)]">{maxPoints.groups}</div>
              <div className="text-[var(--text-dim)]">{t("settings.maxPointsFinals")}</div>
              <div className="text-right font-mono tabular-nums text-[var(--accent-2)]">{maxPoints.finals}</div>
              <div className="text-[var(--text-dim)]">{t("settings.maxPointsTopscorer")}</div>
              <div className="text-right font-mono tabular-nums text-[var(--accent-2)]">
                {maxPoints.topscorer == null ? t("settings.maxPointsTopscorerUnknown") : maxPoints.topscorer}
              </div>
              <div className="col-span-2 border-t border-[var(--line-2)] my-1" />
              <div className="font-semibold">{t("settings.maxPointsTotal")}</div>
              <div className="text-right font-mono tabular-nums font-semibold text-[var(--accent)]">{maxPoints.total}</div>
            </div>
          </div>
          {isOwner && (
            <button data-testid="btn-save-rules" type="button" onClick={saveRules} className="btn-primary mt-6">
              {t("settings.saveRules")}
            </button>
          )}
        </section>

        <section className={SETTINGS_SECTION_DIVIDER}>
          <h3 className={SETTINGS_SECTION_TITLE}>{t("settings.sectionInvite")}</h3>
          <div className="space-y-6">
            <div>
              <Label className={SETTINGS_LABEL}>{t("settings.inviteCode")}</Label>
              <p className={`${SETTINGS_HINT} mt-1 mb-2`}>{t("settings.inviteCodeHint")}</p>
              <div className="flex gap-2">
                <Input value={project.invite_code} readOnly className="bg-[var(--bg-2)] border-[var(--line-2)] font-mono text-sm" />
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(project.invite_code);
                    toast.success(t("common.copied"));
                  }}
                  className="btn-ghost shrink-0"
                >
                  {t("common.copy")}
                </button>
              </div>
            </div>
            <div>
              <Label className={SETTINGS_LABEL}>{t("settings.inviteLinkLabel")}</Label>
              <p className={`${SETTINGS_HINT} mt-1 mb-2`}>{t("settings.inviteLinkHint")}</p>
              <div className="flex gap-2">
                <Input
                  value={inviteLink}
                  readOnly
                  className="bg-[var(--bg-2)] border-[var(--line-2)] font-mono text-sm min-w-0"
                />
                <button
                  type="button"
                  data-testid="btn-settings-copy-invite-link"
                  onClick={() => {
                    if (inviteLink) {
                      navigator.clipboard.writeText(inviteLink);
                      toast.success(t("project.toastInviteLinkCopied"));
                    }
                  }}
                  className="btn-ghost shrink-0"
                  disabled={!inviteLink}
                >
                  {t("common.copy")}
                </button>
              </div>
            </div>
          </div>
        </section>

        {isOwner && (
          <section className={`${SETTINGS_SECTION_DIVIDER} border-[var(--destructive-border)] space-y-3`}>
            <h3 className="text-sm font-semibold text-danger uppercase tracking-wider">{t("settings.sectionDelete")}</h3>
            <p className={SETTINGS_HINT}>{t("settings.deleteHint")}</p>
            <Dialog open={deleteOpen} onOpenChange={(o) => { setDeleteOpen(o); if (!o) setDeleteNameInput(""); }}>
              <DialogTrigger asChild>
                <button
                  type="button"
                  data-testid="btn-open-delete-project"
                  className="btn-ghost btn-destructive text-sm flex items-center gap-2"
                >
                  <Trash2 size={14} /> {t("settings.deleteOpen")}
                </button>
              </DialogTrigger>
              <DialogContent className="bg-[var(--panel)] border-[var(--line-2)]">
                <DialogHeader>
                  <DialogTitle>{t("settings.deleteDialogTitle")}</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-[var(--text-dim)]">
                  {t("settings.deleteDialogBody")} <span className="font-mono text-[var(--text)]">{project.name}</span>
                </p>
                <Input
                  data-testid="input-delete-confirm-name"
                  value={deleteNameInput}
                  onChange={(e) => setDeleteNameInput(e.target.value)}
                  placeholder={t("settings.deletePh")}
                  className="bg-[var(--bg-2)] border-[var(--line-2)]"
                  autoComplete="off"
                />
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" className="btn-ghost text-sm" onClick={() => setDeleteOpen(false)}>
                    {t("common.cancel")}
                  </button>
                  <button
                    type="button"
                    data-testid="btn-confirm-delete-project"
                    disabled={!nameMatches || deleting}
                    onClick={confirmDelete}
                    className="btn-destructive-solid disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {deleting ? t("settings.deleteBusy") : t("settings.deleteConfirm")}
                  </button>
                </div>
              </DialogContent>
            </Dialog>
          </section>
        )}
      </div>
    </div>
  );
}
